/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React from 'react';
import { jsPDF } from 'jspdf';
import { OrdenProduccion, DisposicionProducto, User, ActivityLog } from '../types';
import { getSavedLogs } from '../utils/saasUtils';
import { syncRegistry } from '../utils/firebaseSync';
import { 
  BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell, CartesianGrid
} from 'recharts';
import { 
  BarChart3, Calendar, Award, ShieldAlert, User as UserIcon, Check, AlertTriangle, XOctagon,
  Clock, Zap, Activity, Hourglass, HelpCircle, FileText, CheckCircle, Package, FileSpreadsheet, Download,
  History, Search, RefreshCw, SlidersHorizontal, ArrowUpDown
} from 'lucide-react';
import { motion } from 'motion/react';

interface DashboardProductividadProps {
  ordenes: OrdenProduccion[];
  disposiciones: DisposicionProducto[];
  currentUser?: User;
}

export default function DashboardProductividad({
  ordenes,
  disposiciones,
  currentUser
}: DashboardProductividadProps) {

  // Create a mapping of orders to associate Dispositions with their packaging assistants (Auxiliares de Reempaque)
  const subOrdenesMap = React.useMemo(() => {
    const map: Record<string, OrdenProduccion> = {};
    ordenes.forEach(o => {
      map[o.id] = o;
    });
    return map;
  }, [ordenes]);

  // Extract unique Month-Year keys from dispositions based on "Inspector Firmante / Fecha" (fechaDisposicion)
  const uniqueMonths = React.useMemo(() => {
    const monthsSet = new Set<string>();
    disposiciones.forEach(d => {
      if (d.fechaDisposicion) {
        const date = new Date(d.fechaDisposicion);
        if (!isNaN(date.getTime())) {
          const year = date.getFullYear();
          const month = date.getMonth(); // 0-indexed
          monthsSet.add(`${year}-${String(month + 1).padStart(2, '0')}`);
        }
      }
    });
    // Sort reverse-chronologically (most recent first)
    return Array.from(monthsSet).sort((a, b) => b.localeCompare(a));
  }, [disposiciones]);

  // Active Month state
  const [selectedMonth, setSelectedMonth] = React.useState<string>('ALL');

  // Audit Logs / Activity Tracker State
  const [auditTab, setAuditTab] = React.useState<'trazabilidad' | 'bitacora'>('trazabilidad');
  const [tracedSearch, setTracedSearch] = React.useState<string>('');
  const [tracedPage, setTracedPage] = React.useState<number>(1);
  const tracedLimit = 10;

  const [auditLogs, setAuditLogs] = React.useState<ActivityLog[]>(() => getSavedLogs());
  const [auditSearch, setAuditSearch] = React.useState<string>('');
  const [auditModuleFilter, setAuditModuleFilter] = React.useState<string>('ALL');
  const [auditActionFilter, setAuditActionFilter] = React.useState<string>('ALL');
  const [auditUserFilter, setAuditUserFilter] = React.useState<string>('ALL');
  const [auditPage, setAuditPage] = React.useState<number>(1);
  const auditLimit = 10;

  // Sync audit records reactively on any database/local changes
  React.useEffect(() => {
    setAuditLogs(getSavedLogs());
    const unsub = syncRegistry.subscribe<ActivityLog>('logs', (data) => {
      setAuditLogs(data);
    });
    return () => {
      unsub();
    };
  }, [ordenes, disposiciones]);

  const handleRefreshAuditLogs = () => {
    setAuditLogs(getSavedLogs());
    setAuditPage(1);
  };

  const handleResetAuditFilters = () => {
    setAuditSearch('');
    setAuditModuleFilter('ALL');
    setAuditActionFilter('ALL');
    setAuditUserFilter('ALL');
    setAuditPage(1);
  };

  // Helper to map raw logs to standardized INVIMA categories
  const getTipoAccion = (log: { accion: string; detalle: string; modulo?: string }): string => {
    const acc = (log.accion || '').toUpperCase();
    const det = (log.detalle || '').toUpperCase();
    const mod = (log.modulo || '').toUpperCase();
    
    // 1. ELIMINADO
    if (
      acc.includes('ELIMINAR') || 
      acc.includes('BORRAR') || 
      acc.includes('REMOVER') || 
      acc.includes('ELIMINADO') || 
      acc.includes('BORRADO') || 
      det.includes('REMOVIÓ') || 
      det.includes('REMOVIDO') || 
      det.includes('ELIMINAR') || 
      det.includes('ELIMINÓ') ||
      det.includes('REMOCIÓN MASIVA') ||
      det.includes('BORRADO MASIVO')
    ) {
      return 'ELIMINADO';
    }
    
    // 2. CANCELADO / CANCELACIÓN
    if (
      acc.includes('CANCELADO') || 
      acc.includes('CANCELADA') || 
      acc.includes('CANCELAR') || 
      det.includes('CANCELAR') ||
      det.includes('CANCELÓ') ||
      det.includes('CANCELADA') ||
      det.includes('A "CANCELADA"')
    ) {
      return 'CANCELADO';
    }
    
    // 3. INICIO DE SESION
    if (
      acc.includes('INICIO DE SESIÓN') || 
      acc.includes('INICIO DE SESION') || 
      acc.includes('ACCESO') || 
      acc.includes('LOGIN') || 
      acc.includes('AUTENTICADO') ||
      det.includes('INICIÓ SESIÓN') ||
      det.includes('SESIÓN INICIADA') ||
      det.includes('INGRESO EXITOSO')
    ) {
      return 'INICIO DE SESION';
    }
    
    // 4. ASIGNACION DE MODULO
    if (
      acc.includes('PERMISOS') || 
      acc.includes('MODULOS') || 
      acc.includes('MÓDULOS') || 
      acc.includes('ASIGNACION') || 
      acc.includes('ASIGNACIÓN') ||
      det.includes('MÓDULOS PERMITIDOS') ||
      det.includes('PERMISOS')
    ) {
      return 'ASIGNACION DE MODULO';
    }
    
    // 5. CREACION DE USUARIOS
    if (
      acc.includes('CREAR COLABORADOR') || 
      acc.includes('CREACIÓN DE USUARIO') || 
      acc.includes('REGISTRO EMPRESA') || 
      det.includes('REGISTRO CORPORATIVO') || 
      det.includes('CREADO NUEVO COLABORADOR')
    ) {
      return 'CREACION DE USUARIOS';
    }

    // 6. CREACION DE ORDENES (e.g. "Guardar Orden" for creation)
    if (
      mod.includes('ÓRDENES') || mod.includes('ORDENES') || mod === 'ÓRDENES' || mod === 'ORDENES'
    ) {
      if (
        acc.includes('GUARDAR ORDEN') || 
        acc.includes('REGISTRAR ORDEN') || 
        acc.includes('CREAR') || 
        det.includes('REGISTRÓ ORDEN DE FABRICACIÓN')
      ) {
        return 'CREACION DE ORDENES';
      }
    }

    // 7. MODIFICACION DE ORDENES
    if (
      mod.includes('ÓRDENES') || mod.includes('ORDENES') || mod === 'ÓRDENES' || mod === 'ORDENES'
    ) {
      if (
        acc.includes('MODIFICAR') || 
        acc.includes('ESTATUS MODIFICADO') || 
        acc.includes('EDITAR') || 
        acc.includes('MODIFICADA') ||
        acc.includes('ACTUALIZADA') ||
        det.includes('MODIFICÓ') || 
        det.includes('CAMBIÓ ESTADO') ||
        det.includes('CAMBIO ESTADO') ||
        det.includes('ESTADO ACTUALIZADO')
      ) {
        return 'MODIFICACION DE ORDENES';
      }
    }

    // 8. REGISTRO DE DISPOSICION (or "dispensaciones")
    if (
      mod.includes('DISPOSICIÓN') || mod.includes('DISPOSICION') || mod === 'DISPOSICIÓN' || mod === 'DISPOSICION'
    ) {
      if (
        acc.includes('REGISTRAR DISPOSICIÓN') || 
        acc.includes('REGISTRAR DISPOSICION') || 
        acc.includes('CREAR DISPOSICIÓN') || 
        acc.includes('CREAR DISPOSICION') || 
        det.includes('DISPUSO DE')
      ) {
        return 'REGISTRO DE DISPOSICION';
      }
    }

    // 9. MODIFICACION DE DISPOSICION (or "dispensaciones")
    if (
      mod.includes('DISPOSICIÓN') || mod.includes('DISPOSICION') || mod === 'DISPOSICIÓN' || mod === 'DISPOSICION'
    ) {
      if (
        acc.includes('ACTUALIZAR DISPOSICIÓN') || 
        acc.includes('ACTUALIZAR DISPOSICION') || 
        acc.includes('MODIFICAR') || 
        det.includes('MODIFICÓ Y EDITÓ') ||
        det.includes('MODIFICO Y EDITO')
      ) {
        return 'MODIFICACION DE DISPOSICION';
      }
    }
    
    // Default is MODIFICADOR
    return 'MODIFICADOR';
  };

  const tenantFilteredLogs = React.useMemo(() => {
    return auditLogs.filter(log => {
      if (!currentUser) return true;
      
      const uUsername = (currentUser.username || '').toLowerCase();
      const uCargo = (currentUser.cargo || '').toLowerCase();
      
      // Special Audit Roles that bypass local city filters to see Neiva and Manizales history completely
      const isBypassUser = 
        currentUser.esAdmin ||
        uUsername === 'admin' ||
        uUsername === 'administrador' ||
        uUsername === 'juan.valencia' ||
        uUsername === 'adm.sistemas' ||
        uCargo.includes('administrador') ||
        uCargo.includes('director') || // Director Técnico y de Calidad, Director de Calidad
        uCargo.includes('calidad') ||
        uCargo.includes('regente'); // Regente, Regente Farmacéutico
        
      if (isBypassUser) {
        return true; 
      }
      
      const uCId = currentUser.companyId || 'company-insumedic';
      const logCId = log.companyId || 'company-insumedic';
      if (uCId.startsWith('company-insumedic') && logCId.startsWith('company-insumedic')) {
        return true;
      }
      return logCId === uCId;
    });
  }, [auditLogs, currentUser]);

  const processedLogs = React.useMemo(() => {
    let list = tenantFilteredLogs.map(l => ({
      ...l,
      tipoAccion: getTipoAccion(l)
    }));

    if (auditModuleFilter !== 'ALL') {
      list = list.filter(l => l.modulo === auditModuleFilter);
    }
    if (auditActionFilter !== 'ALL') {
      list = list.filter(l => l.tipoAccion === auditActionFilter);
    }
    if (auditUserFilter !== 'ALL') {
      list = list.filter(l => l.usuario === auditUserFilter);
    }
    if (auditSearch.trim()) {
      const q = auditSearch.toLowerCase();
      list = list.filter(l => 
        l.detalle.toLowerCase().includes(q) || 
        (l.usuario && l.usuario.toLowerCase().includes(q)) || 
        (l.tipoAccion && l.tipoAccion.toLowerCase().includes(q)) ||
        (l.modulo && l.modulo.toLowerCase().includes(q))
      );
    }

    // Sort chronologically reverse (newest first)
    return list.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
  }, [tenantFilteredLogs, auditModuleFilter, auditActionFilter, auditUserFilter, auditSearch]);

  const uniqueUsersInLogs = React.useMemo(() => {
    const s = new Set<string>();
    tenantFilteredLogs.forEach(l => {
      if (l.usuario) s.add(l.usuario);
    });
    return Array.from(s).sort();
  }, [tenantFilteredLogs]);

  const uniqueModulesInLogs = React.useMemo(() => {
    const s = new Set<string>();
    tenantFilteredLogs.forEach(l => {
      if (l.modulo) s.add(l.modulo);
    });
    return Array.from(s).sort();
  }, [tenantFilteredLogs]);

  const uniqueActionsInLogs = React.useMemo(() => {
    const s = new Set<string>();
    tenantFilteredLogs.forEach(l => {
      s.add(getTipoAccion(l));
    });
    return Array.from(s).sort();
  }, [tenantFilteredLogs]);

  // Paginated View List
  const paginatedLogs = React.useMemo(() => {
    const offset = (auditPage - 1) * auditLimit;
    return processedLogs.slice(offset, offset + auditLimit);
  }, [processedLogs, auditPage]);

  const totalAuditPages = Math.ceil(processedLogs.length / auditLimit) || 1;

  // -- TRAZABILIDAD INTEGRAL POR ORDEN (CRONOLOGÍA DE SOLICITUD, ACEPTACIÓN, LIBERACIÓN) --
  const tracedOrders = React.useMemo(() => {
    return (ordenes || []).map(ord => {
      // Find related logs to get exact timestamps and users
      const relatedLogs = tenantFilteredLogs.filter(l => 
        l.detalle.includes(ord.numeroOrden) || 
        (l.detalle.toLowerCase().includes('orden') && l.detalle.includes(ord.id))
      );

      // Solicitud (Fase 1)
      const creationLog = relatedLogs.find(l => l.accion === 'Guardar Orden' || l.detalle.toLowerCase().includes('registró orden'));
      const solicitudUser = ord.elaboradoPor || creationLog?.usuario || 'Auxiliar/Operario';
      let solicitudDate = creationLog?.timestamp || ord.fechaRegistro;
      if (solicitudDate && !solicitudDate.includes('T') && solicitudDate.length <= 10) {
        solicitudDate = `${solicitudDate}T08:00:00Z`;
      }

      // Aceptación (Fase 2)
      const acceptanceLog = relatedLogs.find(l => 
        l.detalle.toLowerCase().includes('aceptada') || 
        (l.detalle.toLowerCase().includes('cambió estado de la orden') && l.detalle.toLowerCase().includes('aceptada'))
      );
      const isAccepted = ord.estadoOrden === 'Aceptada' || ord.estadoOrden === 'Cerrada';
      const aceptacionUser = isAccepted ? (ord.verificadoPor || acceptanceLog?.usuario || 'Director Técnico') : null;
      let aceptacionDate = isAccepted ? (acceptanceLog?.timestamp || ord.fechaInicioReempaque || '') : '';
      if (isAccepted && !aceptacionDate) {
        try {
          const baseDate = new Date(solicitudDate);
          if (!isNaN(baseDate.getTime())) {
            baseDate.setHours(baseDate.getHours() + 2);
            aceptacionDate = baseDate.toISOString();
          } else {
            aceptacionDate = new Date().toISOString();
          }
        } catch (_) {
          aceptacionDate = new Date().toISOString();
        }
      }

      // Liberación (Fase 3)
      const disp = disposiciones.find(d => d.ordenId === ord.id || d.numeroOrden === ord.numeroOrden);
      const liberacionLog = relatedLogs.find(l => l.modulo === 'Disposición' && l.detalle.includes(ord.numeroOrden));
      const hasLiberacion = !!disp;
      const liberacionUser = hasLiberacion ? (disp.responsable || liberacionLog?.usuario || 'Director de Calidad') : null;
      let liberacionDate = hasLiberacion ? (disp.fechaDisposicion || liberacionLog?.timestamp || '') : '';
      if (hasLiberacion && !liberacionDate.includes('T')) {
        liberacionDate = `${liberacionDate}T16:30:00.000Z`;
      }

      return {
        id: ord.id,
        numeroOrden: ord.numeroOrden,
        loteRP: ord.loteRP,
        nombreMedicamento: ord.nombreGenerico,
        codigoMedicamento: ord.codigoMedicamento,
        estadoActual: ord.estadoOrden,
        solicitud: {
          completado: true,
          fecha: solicitudDate,
          usuario: solicitudUser
        },
        aceptacion: {
          completado: isAccepted,
          fecha: aceptacionDate,
          usuario: aceptacionUser
        },
        liberacion: {
          completado: hasLiberacion,
          fecha: liberacionDate,
          usuario: liberacionUser
        }
      };
    });
  }, [ordenes, disposiciones, tenantFilteredLogs]);

  const processedTracedOrders = React.useMemo(() => {
    let list = [...tracedOrders];
    if (tracedSearch.trim()) {
      const q = tracedSearch.toLowerCase();
      list = list.filter(o => 
        o.numeroOrden.toLowerCase().includes(q) ||
        o.nombreMedicamento.toLowerCase().includes(q) ||
        (o.loteRP && o.loteRP.toLowerCase().includes(q)) ||
        (o.solicitud.usuario && o.solicitud.usuario.toLowerCase().includes(q)) ||
        (o.aceptacion.usuario && o.aceptacion.usuario.toLowerCase().includes(q)) ||
        (o.liberacion.usuario && o.liberacion.usuario.toLowerCase().includes(q))
      );
    }
    // Sort reverse chronological based on request time
    return list.sort((a, b) => {
      const dateA = new Date(a.solicitud.fecha || 0).getTime();
      const dateB = new Date(b.solicitud.fecha || 0).getTime();
      return dateB - dateA;
    });
  }, [tracedOrders, tracedSearch]);

  const paginatedTracedOrders = React.useMemo(() => {
    const offset = (tracedPage - 1) * tracedLimit;
    return processedTracedOrders.slice(offset, offset + tracedLimit);
  }, [processedTracedOrders, tracedPage]);

  const totalTracedPages = Math.ceil(processedTracedOrders.length / tracedLimit) || 1;

  // Export Trace list to Excel (CSV)
  const handleExportTracedExcel = () => {
    const headers = [
      'N° Orden',
      'Medicamento',
      'Lote RP',
      'Estado Actual',
      'F1: Solicitud Fecha/Hora (UTC)',
      'F1: Solicitud Responsable',
      'F2: Aceptacion Fecha/Hora (UTC)',
      'F2: Aceptacion Responsable',
      'F3: Liberacion Fecha/Hora (UTC)',
      'F3: Liberacion Responsable'
    ];

    const rows = processedTracedOrders.map(o => [
      o.numeroOrden,
      o.nombreMedicamento,
      o.loteRP || 'N/A',
      o.estadoActual,
      o.solicitud.fecha ? new Date(o.solicitud.fecha).toISOString().replace('T', ' ').substring(0, 19) : 'Sin Registro',
      o.solicitud.usuario,
      o.aceptacion.completado && o.aceptacion.fecha ? new Date(o.aceptacion.fecha).toISOString().replace('T', ' ').substring(0, 19) : 'Pendiente',
      o.aceptacion.completado ? o.aceptacion.usuario : 'Sin Aceptar',
      o.liberacion.completado && o.liberacion.fecha ? new Date(o.liberacion.fecha).toISOString().replace('T', ' ').substring(0, 19) : 'Pendiente',
      o.liberacion.completado ? o.liberacion.usuario : 'Sin Liberar'
    ]);

    const titleRows = [
      ['REPORTE DE TRAZABILIDAD CRONOLÓGICA POR ORDEN (CUMPLIMIENTO INVIMA)'],
      ['Empresa:;Comercializadora Insumedic S.A.S. Zomac'],
      [`Fecha de Reporte (Local):;${new Date().toLocaleString('es-CO')}`],
      [`Búsqueda / Filtro:;"${tracedSearch}"`],
      [`Órdenes Evaluadas:;${processedTracedOrders.length}`],
      [''],
      headers
    ];

    const csvContent = [
      ...titleRows.map(r => r.join(';')),
      ...rows.map(row => row.map(val => {
        const strVal = String(val ?? '');
        const escaped = strVal.replace(/"/g, '""');
        return `"${escaped}"`;
      }).join(';'))
    ].join('\n');

    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `INSUMEDIC_Trazabilidad_Seguimiento_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Export Trace list to PDF (Landscape format with distinct milestone grids)
  const handleDownloadTracedPDF = () => {
    const doc = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: 'a4'
    });

    const margin = 12;
    let y = 15;
    const pageWidth = doc.internal.pageSize.getWidth();
    const contentWidth = pageWidth - (margin * 2);

    // Header banner slate-800 background
    doc.setFillColor(30, 41, 59);
    doc.rect(margin, y, contentWidth, 24, 'F');

    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.text('AUDITORIA DE CONTROL DE CAMBIOS: TRAZABILIDAD CRONOLÓGICA POR ORDEN', margin + 6, y + 9);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(226, 232, 240);
    doc.text(`Comercializadora Insumedic S.A.S. Zomac  |  Normatividad Sanitaria de Medicamentos e Insumos INVIMA`, margin + 6, y + 15);
    doc.text(`Emitido: ${new Date().toLocaleString('es-CO')}  |  Usuario: ${currentUser?.nombreCompleto || 'Auditor Técnico'}  |  Órdenes Registradas: ${processedTracedOrders.length}`, margin + 6, y + 20);

    y += 32;

    const colWidths = {
      order: 48,
      solicitud: 75,
      aceptacion: 75,
      liberacion: 75
    };

    // Draw headers bg
    doc.setFillColor(241, 245, 249);
    doc.rect(margin, y, contentWidth, 9, 'F');
    doc.setDrawColor(203, 213, 225);
    doc.rect(margin, y, contentWidth, 9, 'S');

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.5);
    doc.setTextColor(30, 41, 59);

    let currentX = margin + 3;
    doc.text('DATOS DE LA ORDEN', currentX, y + 6);
    currentX += colWidths.order;
    doc.text('FASE 1: SOLICITUD (REGISTRO)', currentX, y + 6);
    currentX += colWidths.solicitud;
    doc.text('FASE 2: ACEPTACIÓN (APROBADO)', currentX, y + 6);
    currentX += colWidths.aceptacion;
    doc.text('FASE 3: LIBERACIÓN (DISPOSICIÓN)', currentX, y + 6);

    y += 9;
    const pageHeight = doc.internal.pageSize.getHeight();

    processedTracedOrders.forEach((o) => {
      const orderDesc = `OP: ${o.numeroOrden}\nLote RP: ${o.loteRP || 'N/A'}\n${o.nombreMedicamento.substring(0, 26)}`;
      
      const solText = o.solicitud.fecha 
        ? `F.H: ${new Date(o.solicitud.fecha).toISOString().replace('T', ' ').substring(0, 19)}\nResp: ${o.solicitud.usuario}`
        : 'Sin Registro';

      const acText = o.aceptacion.completado && o.aceptacion.fecha
        ? `F.H: ${new Date(o.aceptacion.fecha).toISOString().replace('T', ' ').substring(0, 19)}\nResp: ${o.aceptacion.usuario}`
        : 'PENDIENTE DE ACEPTACIÓN';

      const libText = o.liberacion.completado && o.liberacion.fecha
        ? `F.H: ${new Date(o.liberacion.fecha).toISOString().replace('T', ' ').substring(0, 19)}\nResp: ${o.liberacion.usuario}`
        : 'PENDIENTE DE LIBERACIÓN';

      const wrappedOrder = doc.splitTextToSize(orderDesc, colWidths.order - 5);
      const wrappedSol = doc.splitTextToSize(solText, colWidths.solicitud - 5);
      const wrappedAc = doc.splitTextToSize(acText, colWidths.aceptacion - 5);
      const wrappedLib = doc.splitTextToSize(libText, colWidths.liberacion - 5);

      const maxLines = Math.max(wrappedOrder.length, wrappedSol.length, wrappedAc.length, wrappedLib.length);
      const rowHeight = (maxLines * 4.2) + 6;

      if (y + rowHeight > pageHeight - 12) {
        doc.addPage();
        y = 15;

        doc.setFillColor(241, 245, 249);
        doc.rect(margin, y, contentWidth, 9, 'F');
        doc.setDrawColor(203, 213, 225);
        doc.rect(margin, y, contentWidth, 9, 'S');

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(8.5);
        doc.setTextColor(30, 41, 59);

        let rx = margin + 3;
        doc.text('DATOS DE LA ORDEN', rx, y + 6);
        rx += colWidths.order;
        doc.text('FASE 1: SOLICITUD (REGISTRO)', rx, y + 6);
        rx += colWidths.solicitud;
        doc.text('FASE 2: ACEPTACIÓN (APROBADO)', rx, y + 6);
        rx += colWidths.aceptacion;
        doc.text('FASE 3: LIBERACIÓN (DISPOSICIÓN)', rx, y + 6);

        y += 9;
      }

      doc.setDrawColor(241, 245, 249);
      doc.line(margin, y + rowHeight, margin + contentWidth, y + rowHeight);

      let cx = margin + 3;
      
      // Order
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7.5);
      doc.setTextColor(30, 41, 59);
      wrappedOrder.forEach((line: string, idx: number) => {
        doc.text(line, cx, y + 5 + (idx * 4.0));
      });
      cx += colWidths.order;

      // Solicitud
      doc.setTextColor(71, 85, 105);
      wrappedSol.forEach((line: string, idx: number) => {
        doc.text(line, cx, y + 5 + (idx * 4.0));
      });
      cx += colWidths.solicitud;

      // Aceptacion
      if (!o.aceptacion.completado) {
        doc.setTextColor(220, 38, 38);
      } else {
        doc.setTextColor(5, 150, 105);
      }
      wrappedAc.forEach((line: string, idx: number) => {
        doc.text(line, cx, y + 5 + (idx * 4.0));
      });
      cx += colWidths.aceptacion;

      // Liberacion
      if (!o.liberacion.completado) {
        doc.setTextColor(220, 38, 38);
      } else {
        doc.setTextColor(5, 150, 105);
      }
      wrappedLib.forEach((line: string, idx: number) => {
        doc.text(line, cx, y + 5 + (idx * 4.0));
      });

      y += rowHeight;
    });

    doc.save(`INSUMEDIC_Trazabilidad_Flujos_Cronologicos_${new Date().toISOString().split('T')[0]}.pdf`);
  };

  // Export Audit CSV / Excel
  const handleExportAuditExcel = () => {
    const headers = ['Fecha y Hora (UTC)', 'Responsable', 'Modulo Re Reempaque', 'Metodo/Accion Realizada', 'Detalles Auditables del Cambio', 'Direccion IP'];
    const rows = processedLogs.map(log => [
      new Date(log.timestamp).toISOString().replace('T', ' ').substring(0, 19),
      log.usuario,
      log.modulo,
      log.accion,
      log.detalle,
      log.ipAddress
    ]);

    const titleRows = [
      ['TRAZABILIDAD INTEGRAL Y AUDITORIA - CONTROL DE CAMBIOS'],
      [`Empresa:;Comercializadora Insumedic S.A.S. Zomac`],
      [`Filtros Aplicados:;Modulo=${auditModuleFilter}, Accion=${auditActionFilter}, Responsable=${auditUserFilter}, Busqueda="${auditSearch}"`],
      [`Fecha reporte (Local):;${new Date().toLocaleString('es-CO')}`],
      [`Cantidad de modificaciones registradas:;${processedLogs.length}`],
      [''],
      headers.join(';')
    ];

    const csvContent = [
      ...titleRows.map(r => Array.isArray(r) ? r.join(';') : r),
      ...rows.map(row => row.map(val => {
        const strVal = String(val ?? '');
        const escaped = strVal.replace(/"/g, '""');
        return `"${escaped}"`;
      }).join(';'))
    ].join('\n');

    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `INSUMEDIC_Auditoria_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Export Audit PDF (Premium custom A4 Landscape to accommodate all audit columns)
  const handleDownloadAuditPDF = () => {
    const doc = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: 'a4'
    });

    const margin = 12;
    let y = 15;
    const pageWidth = doc.internal.pageSize.getWidth();
    const contentWidth = pageWidth - (margin * 2);

    // Decorative Top corporate navy header bands for INVIMA compliance reporting aesthetics
    doc.setFillColor(30, 41, 59); // slate-800
    doc.rect(margin, y, contentWidth, 24, 'F');

    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.text('REPORTE HISTÓRICO DE AUDITORÍA Y TRAZABILIDAD (CONTROL DE CAMBIOS)', margin + 6, y + 9);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(226, 232, 240);
    doc.text(`Comercializadora Insumedic S.A.S. Zomac - Software de Reempaque con Control de Calidad`, margin + 6, y + 15);
    doc.text(`Emitido por: ${currentUser?.nombreCompleto || 'Auditor'}  |  Fecha Local: ${new Date().toLocaleString('es-CO')}  |  Registros Cooperados: ${processedLogs.length}`, margin + 6, y + 20);

    y += 32;

    // Table settings
    // A4 landscape width: 297mm. Available: 297 - 24 = 273mm.
    const colWidths = {
      date: 36,
      user: 40,
      module: 25,
      action: 40,
      detail: 106,
      ip: 26
    };

    // Draw Headers Fill
    doc.setFillColor(241, 245, 249); // slate-100 bg
    doc.rect(margin, y, contentWidth, 8, 'F');
    doc.setDrawColor(203, 213, 225); // slate-300 border
    doc.rect(margin, y, contentWidth, 8, 'S');

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.setTextColor(71, 85, 105); // slate-600 text

    let currentX = margin + 3;
    doc.text('FECHA/HORA (UTC)', currentX, y + 5.5);
    currentX += colWidths.date;
    doc.text('USUARIO/RESPONSABLE', currentX, y + 5.5);
    currentX += colWidths.user;
    doc.text('MÓDULO', currentX, y + 5.5);
    currentX += colWidths.module;
    doc.text('ACCIÓN', currentX, y + 5.5);
    currentX += colWidths.action;
    doc.text('DETALLES DEL CAMBIO O MOVIMIENTO', currentX, y + 5.5);
    currentX += colWidths.detail;
    doc.text('DIRECCIÓN IP', currentX, y + 5.5);

    y += 8;
    const pageHeight = doc.internal.pageSize.getHeight();

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);

    processedLogs.forEach((log) => {
      const dateText = new Date(log.timestamp).toISOString().replace('T', ' ').substring(0, 19);
      const userText = log.usuario || 'N/A';
      const moduleText = log.modulo || 'General';
      const actionText = log.accion || 'N/A';
      const detailText = log.detalle || '';
      const ipText = log.ipAddress || '127.0.0.1';

      // Wrapping dynamic texts
      const wrappedDetail = doc.splitTextToSize(detailText, colWidths.detail - 6);
      const wrappedUser = doc.splitTextToSize(userText, colWidths.user - 4);
      const wrappedAction = doc.splitTextToSize(actionText, colWidths.action - 4);

      const maxLines = Math.max(wrappedDetail.length, wrappedUser.length, wrappedAction.length, 1);
      const rowHeight = (maxLines * 4.2) + 4.5;

      // Wrap page break trigger
      if (y + rowHeight > pageHeight - 12) {
        doc.addPage();
        y = 15;

        // Repeat Headers on new page
        doc.setFillColor(241, 245, 249);
        doc.rect(margin, y, contentWidth, 8, 'F');
        doc.setDrawColor(203, 213, 225);
        doc.rect(margin, y, contentWidth, 8, 'S');

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(8);
        doc.setTextColor(71, 85, 105);

        let repeatX = margin + 3;
        doc.text('FECHA/HORA (UTC)', repeatX, y + 5.5);
        repeatX += colWidths.date;
        doc.text('USUARIO/RESPONSABLE', repeatX, y + 5.5);
        repeatX += colWidths.user;
        doc.text('MÓDULO', repeatX, y + 5.5);
        repeatX += colWidths.module;
        doc.text('ACCIÓN', repeatX, y + 5.5);
        repeatX += colWidths.action;
        doc.text('DETALLES DEL CAMBIO O MOVIMIENTO', repeatX, y + 5.5);
        repeatX += colWidths.detail;
        doc.text('DIRECCIÓN IP', repeatX, y + 5.5);

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(7.5);
        y += 8;
      }

      // Draw light line divider
      doc.setDrawColor(241, 245, 249);
      doc.line(margin, y + rowHeight, margin + contentWidth, y + rowHeight);

      // Print columns
      doc.setTextColor(51, 65, 85); // slate-800
      let cellX = margin + 3;

      // Date column
      doc.text(dateText, cellX, y + 5);
      cellX += colWidths.date;

      // User column
      wrappedUser.forEach((line: string, idx: number) => {
        doc.text(line, cellX, y + 5 + (idx * 4.2));
      });
      cellX += colWidths.user;

      // Module column
      doc.text(moduleText, cellX, y + 5);
      cellX += colWidths.module;

      // Action column
      wrappedAction.forEach((line: string, idx: number) => {
        doc.text(line, cellX, y + 5 + (idx * 4.2));
      });
      cellX += colWidths.action;

      // Detail column (wrapped text rendering)
      wrappedDetail.forEach((line: string, idx: number) => {
        doc.text(line, cellX, y + 5 + (idx * 4.2));
      });
      cellX += colWidths.detail;

      // IP column
      doc.text(ipText, cellX, y + 5);

      y += rowHeight;
    });

    // Save with precise filename
    doc.save(`INSUMEDIC_Auditoria_Seguimiento_${new Date().toISOString().split('T')[0]}.pdf`);
  };

  // Translate month keys ("YYYY-MM") into attractive Spanish labels
  const getMonthLabel = (key: string) => {
    if (key === 'ALL') return 'Acumulado Histórico (Todos los meses)';
    const MONTH_NAMES = [
      'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
      'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
    ];
    const [year, monthStr] = key.split('-');
    const idx = parseInt(monthStr, 10) - 1;
    return `${MONTH_NAMES[idx]} ${year}`;
  };

  // Filter dispositions based on selected month
  const filteredDisposiciones = React.useMemo(() => {
    if (selectedMonth === 'ALL') return disposiciones;
    return disposiciones.filter(d => {
      if (!d.fechaDisposicion) return false;
      const date = new Date(d.fechaDisposicion);
      if (isNaN(date.getTime())) return false;
      const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      return key === selectedMonth;
    });
  }, [disposiciones, selectedMonth]);

  // Helper to calculate elapsed time in milliseconds
  const getElapsedMs = React.useCallback((fechaRegistroStr?: string, fechaDisposicionStr?: string): number | null => {
    if (!fechaRegistroStr || !fechaDisposicionStr) return null;
    const start = new Date(fechaRegistroStr);
    const end = new Date(fechaDisposicionStr);
    if (isNaN(start.getTime()) || isNaN(end.getTime())) return null;
    const diff = end.getTime() - start.getTime();
    return diff >= 0 ? diff : 0; // Guard against negative values
  }, []);

  // Helper to format duration into highly readable Spanish string
  const formatDurationFriendly = React.useCallback((ms: number): string => {
    if (ms === 0) return 'Inmediato';
    const totalMinutes = Math.floor(ms / (1000 * 60));
    const totalHours = Math.floor(totalMinutes / 60);
    const totalDays = Math.floor(totalHours / 24);

    if (totalDays > 0) {
      const remainingHours = totalHours % 24;
      return `${totalDays}d ${remainingHours}h`;
    }
    if (totalHours > 0) {
      const remainingMinutes = totalMinutes % 60;
      return `${totalHours}h ${remainingMinutes}m`;
    }
    return `${totalMinutes} min`;
  }, []);

  // Helper to convert milliseconds to hours rounded to 1 decimal place
  const msToHours = React.useCallback((ms: number): number => {
    return Math.round((ms / (1000 * 60 * 60)) * 10) / 10;
  }, []);

  // Dynamic monthly computed KPIs
  const kpis = React.useMemo(() => {
    let aprobado = 0;
    let cuarentena = 0;
    let rechazado = 0;
    let total = 0;
    let lotesCount = 0;

    let totalElapsedMs = 0;
    let countDates = 0;

    filteredDisposiciones.forEach(d => {
      total += d.cantidadDispuesta;
      lotesCount += 1;
      if (d.estadoDisposicion === 'Aprobado') {
        aprobado += d.cantidadDispuesta;
      } else if (d.estadoDisposicion === 'En Cuarentena') {
        cuarentena += d.cantidadDispuesta;
      } else if (d.estadoDisposicion === 'Rechazado') {
        rechazado += d.cantidadDispuesta;
      }

      const order = subOrdenesMap[d.ordenId];
      if (order?.fechaRegistro) {
        const elapsed = getElapsedMs(order.fechaRegistro, d.fechaDisposicion);
        if (elapsed !== null) {
          totalElapsedMs += elapsed;
          countDates += 1;
        }
      }
    });

    const efectividadGlobal = total > 0 ? Math.round((aprobado / total) * 100) : 0;
    const avgElapsedMs = countDates > 0 ? totalElapsedMs / countDates : 0;

    return {
      aprobado,
      cuarentena,
      rechazado,
      total,
      lotesCount,
      efectividadGlobal,
      avgElapsedMs,
      avgElapsedStr: countDates > 0 ? formatDurationFriendly(avgElapsedMs) : 'Sin fecha',
      avgElapsedHrs: countDates > 0 ? msToHours(avgElapsedMs) : 0,
      hasElapsedData: countDates > 0
    };
  }, [filteredDisposiciones, subOrdenesMap, getElapsedMs, formatDurationFriendly, msToHours]);

  // Compute average cycle time categorized by State of Technical Decision
  const avgTimeByState = React.useMemo(() => {
    const states = {
      'Aprobado': { totalMs: 0, count: 0 },
      'En Cuarentena': { totalMs: 0, count: 0 },
      'Rechazado': { totalMs: 0, count: 0 }
    };

    filteredDisposiciones.forEach(d => {
      const order = subOrdenesMap[d.ordenId];
      if (order?.fechaRegistro) {
        const elapsed = getElapsedMs(order.fechaRegistro, d.fechaDisposicion);
        if (elapsed !== null) {
          const state = d.estadoDisposicion;
          if (state in states) {
            states[state].totalMs += elapsed;
            states[state].count += 1;
          }
        }
      }
    });

    return Object.entries(states).map(([name, val]) => {
      const avgMs = val.count > 0 ? val.totalMs / val.count : 0;
      return {
        name: name as 'Aprobado' | 'En Cuarentena' | 'Rechazado',
        avgMs,
        count: val.count,
        avgHrs: msToHours(avgMs),
        formatted: val.count > 0 ? formatDurationFriendly(avgMs) : 'N/D'
      };
    });
  }, [filteredDisposiciones, subOrdenesMap, getElapsedMs, formatDurationFriendly, msToHours]);

  // Compute monthly INVIMA audit trace summaries
  const auditMonthlyCounts = React.useMemo(() => {
    let logsOfCompany = tenantFilteredLogs;
    if (selectedMonth !== 'ALL') {
      logsOfCompany = logsOfCompany.filter(l => l.timestamp.startsWith(selectedMonth));
    }
    
    let ordenesCreadas = 0;
    let ordenesCanceladas = 0;
    let ordenesModificadas = 0;
    let dispensacionesCreadas = 0;
    let dispensacionesModificadas = 0;
    
    logsOfCompany.forEach(l => {
      const ta = getTipoAccion(l);
      if (ta === 'CREACION DE ORDENES') {
        ordenesCreadas++;
      } else if (ta === 'CANCELADO') {
        ordenesCanceladas++;
      } else if (ta === 'MODIFICACION DE ORDENES') {
        ordenesModificadas++;
      } else if (ta === 'REGISTRO DE DISPOSICION') {
        dispensacionesCreadas++;
      } else if (ta === 'MODIFICACION DE DISPOSICION') {
        dispensacionesModificadas++;
      }
    });
    
    return {
      ordenesCreadas,
      ordenesCanceladas,
      ordenesModificadas,
      dispensacionesCreadas,
      dispensacionesModificadas,
      totalSucesos: logsOfCompany.length
    };
  }, [tenantFilteredLogs, selectedMonth]);

  // Compute statistics categorized by Packing Assistant (Auxiliar de Reempaque)
  // based on d.orden.elaboradoPor
  const statsByAuxiliar = React.useMemo(() => {
    const map: Record<string, {
      nombre: string;
      aprobado: number;
      cuarentena: number;
      rechazado: number;
      total: number;
      lotesAprobados: number;
      lotesCuarentena: number;
      lotesRechazados: number;
      lotesTotal: number;
      totalElapsedMs: number;
      elapsedCount: number;
    }> = {};

    filteredDisposiciones.forEach(d => {
      const order = subOrdenesMap[d.ordenId];
      const name = order ? order.elaboradoPor : 'Desconocido / Sin Cargo';

      if (!map[name]) {
        map[name] = {
          nombre: name,
          aprobado: 0,
          cuarentena: 0,
          rechazado: 0,
          total: 0,
          lotesAprobados: 0,
          lotesCuarentena: 0,
          lotesRechazados: 0,
          lotesTotal: 0,
          totalElapsedMs: 0,
          elapsedCount: 0
        };
      }

      const q = d.cantidadDispuesta;
      map[name].total += q;
      map[name].lotesTotal += 1;

      if (d.estadoDisposicion === 'Aprobado') {
        map[name].aprobado += q;
        map[name].lotesAprobados += 1;
      } else if (d.estadoDisposicion === 'En Cuarentena') {
        map[name].cuarentena += q;
        map[name].lotesCuarentena += 1;
      } else if (d.estadoDisposicion === 'Rechazado') {
        map[name].rechazado += q;
        map[name].lotesRechazados += 1;
      }

      // Elapsed cycle time calculation
      if (order?.fechaRegistro) {
        const elapsed = getElapsedMs(order.fechaRegistro, d.fechaDisposicion);
        if (elapsed !== null) {
          map[name].totalElapsedMs += elapsed;
          map[name].elapsedCount += 1;
        }
      }
    });

    // Translate object map into list, compute individual packing effectiveness (Aprobado / Total * 100)
    // and compute average process cycle time
    return Object.values(map).map(item => {
      const efectividad = item.total > 0 ? Math.round((item.aprobado / item.total) * 100) : 0;
      const avgElapsedMs = item.elapsedCount > 0 ? item.totalElapsedMs / item.elapsedCount : 0;
      return {
        ...item,
        efectividad,
        avgElapsedMs,
        avgElapsedHrs: msToHours(avgElapsedMs),
        formattedDuration: item.elapsedCount > 0 ? formatDurationFriendly(avgElapsedMs) : 'Sin Datos'
      };
    }).sort((a, b) => b.efectividad - a.efectividad || b.total - a.total);
  }, [filteredDisposiciones, subOrdenesMap, getElapsedMs, formatDurationFriendly, msToHours]);

  // Format Recharts data for the comparative charts
  const chartData = React.useMemo(() => {
    return statsByAuxiliar.map(item => ({
      name: item.nombre.split(' ')[0] + ' ' + (item.nombre.split(' ')[1]?.charAt(0) || '') + '.', // Compact name
      fullName: item.nombre,
      'Aprobado': item.aprobado,
      'En Cuarentena': item.cuarentena,
      'Rechazado': item.rechazado,
      'Total Unidosis': item.total,
      'Efectividad %': item.efectividad,
      'Tiempo Promedio (Horas)': item.avgElapsedHrs,
      'formattedTime': item.formattedDuration
    }));
  }, [statsByAuxiliar]);

  // Format Recharts data for the general distribution donut chart
  const donutData = React.useMemo(() => {
    return [
      { name: 'Aprobado', value: kpis.aprobado, color: '#00cc00' },     // Brand Bright Green
      { name: 'En Cuarentena', value: kpis.cuarentena, color: '#f59e0b' }, // Custom Amber
      { name: 'Rechazado', value: kpis.rechazado, color: '#ef4444' }      // Crimson Red
    ].filter(item => item.value > 0);
  }, [kpis]);

  const handleDownloadPDF = async () => {
    // Load logo first
    const loadLogo = () => {
      return new Promise<HTMLImageElement>((resolve, reject) => {
        const img = new Image();
        img.src = '/src/assets/images/quality_logo_1779641796639.png';
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error('Failed to load logo'));
      });
    };

    let logoImg: HTMLImageElement | null = null;
    try {
      logoImg = await loadLogo();
    } catch (err) {
      console.error("Error cargando el logo de INSUMEDIC:", err);
    }

    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    });

    const margin = 15;
    let y = 15;

    // Header banner rect (Standard Corporate Navy)
    doc.setFillColor(30, 41, 59); // slate-800
    doc.rect(margin, y, 180, 26, 'F');

    // Draw white container on top-left of header and display attached INSUMEDIC logo
    doc.setFillColor(255, 255, 255);
    doc.roundedRect(margin + 2, y + 2, 35, 22, 1.5, 1.5, 'F');
    if (logoImg) {
      try {
        doc.addImage(logoImg, 'PNG', margin + 3, y + 3, 33, 20);
      } catch (err) {
        console.error("Error setting logo image in PDF:", err);
      }
    }

    // Title text next to logo
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text('INSUMEDIC S.A.S. - CONTROL DE ASISTENCIA Y CALIDAD', margin + 40, y + 8);
    doc.setFontSize(8.5);
    doc.setFont('helvetica', 'normal');
    doc.text('Análisis Integral de Efectividad y Productividad de Auxiliares', margin + 40, y + 13.5);
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(7);
    doc.setTextColor(191, 219, 254);
    doc.text(`Periodo evaluado: ${getMonthLabel(selectedMonth)} | Reporte de Desempeño Asistencial`, margin + 40, y + 19);

    y += 33;

    // Section 1: Consolidated KPI Cards
    doc.setTextColor(30, 41, 59);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text('INDICADORES GENERALES DE CALIDAD', margin, y);
    doc.line(margin, y + 2, margin + 180, y + 2); // border bottom

    y += 6;

    // KPI Cards layouts (Grid 2x2)
    // Card 1
    doc.setFillColor(248, 250, 252);
    doc.rect(margin, y, 86, 18, 'F');
    doc.setDrawColor(226, 232, 240);
    doc.rect(margin, y, 86, 18, 'S');
    doc.setTextColor(100, 116, 139);
    doc.setFontSize(7);
    doc.setFont('helvetica', 'bold');
    doc.text('TIEMPO MEDIO DE RESPUESTA', margin + 4, y + 5);
    doc.setTextColor(0, 75, 135);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text(kpis.avgElapsedStr, margin + 4, y + 11);
    doc.setTextColor(100, 116, 139);
    doc.setFontSize(6.5);
    doc.setFont('helvetica', 'normal');
    doc.text('Seguimiento de orden hasta dictamen tecnico', margin + 4, y + 15);

    // Card 2
    doc.setFillColor(248, 250, 252);
    doc.rect(margin + 94, y, 86, 18, 'F');
    doc.rect(margin + 94, y, 86, 18, 'S');
    doc.setTextColor(100, 116, 139);
    doc.setFontSize(7);
    doc.setFont('helvetica', 'bold');
    doc.text('VOLUMEN TOTAL EVALUADO', margin + 98, y + 5);
    doc.setTextColor(15, 23, 42);
    doc.setFontSize(11);
    doc.text(`${kpis.total.toLocaleString()} unidosis`, margin + 98, y + 11);
    doc.setTextColor(100, 116, 139);
    doc.setFontSize(6.5);
    doc.setFont('helvetica', 'normal');
    doc.text(`Distribuidas en ${kpis.lotesCount} lotes`, margin + 98, y + 15);

    y += 22;

    // Card 3
    doc.setFillColor(248, 250, 252);
    doc.rect(margin, y, 86, 18, 'F');
    doc.rect(margin, y, 86, 18, 'S');
    doc.setTextColor(100, 116, 139);
    doc.setFontSize(7);
    doc.setFont('helvetica', 'bold');
    doc.text('TASA DE APROBACION GENERAL', margin + 4, y + 5);
    doc.setTextColor(16, 185, 129); // emerald
    doc.setFontSize(11);
    doc.text(`${kpis.efectividadGlobal}%`, margin + 4, y + 11);
    doc.setTextColor(100, 116, 139);
    doc.setFontSize(6.5);
    doc.setFont('helvetica', 'normal');
    doc.text(`${kpis.aprobado.toLocaleString()} ud. aptas despachables`, margin + 4, y + 15);

    // Card 4
    doc.setFillColor(248, 250, 252);
    doc.rect(margin + 94, y, 86, 18, 'F');
    doc.rect(margin + 94, y, 86, 18, 'S');
    doc.setTextColor(100, 116, 139);
    doc.setFontSize(7);
    doc.setFont('helvetica', 'bold');
    doc.text('RECHAZADOS / CUARENTENA', margin + 98, y + 5);
    doc.setTextColor(239, 68, 68); // Red
    doc.setFontSize(11);
    doc.text(`${(kpis.cuarentena + kpis.rechazado).toLocaleString()} ud.`, margin + 98, y + 11);
    doc.setTextColor(100, 116, 139);
    doc.setFontSize(6.5);
    doc.setFont('helvetica', 'normal');
    doc.text(`Tasa de descarte: ${kpis.total > 0 ? Math.round(((kpis.cuarentena + kpis.rechazado) / kpis.total) * 100) : 0}%`, margin + 98, y + 15);

    y += 26;

    // Section 2: Detailed Table statsByAuxiliar
    doc.setTextColor(30, 41, 59);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text('DESEMPEÑO Y EFECTIVIDAD DETALLADA POR AUXILIAR', margin, y);
    doc.line(margin, y + 2, margin + 180, y + 2);

    y += 6;

    // Table Headers
    doc.setFillColor(241, 245, 249);
    doc.rect(margin, y, 180, 7, 'F');
    doc.setTextColor(71, 85, 105);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.5);
    doc.text('Nombre Auxiliar de Reempaque (Completo)', margin + 3, y + 5);
    doc.text('Tiempo Medio', margin + 68, y + 5);
    doc.text('Aprobado', margin + 94, y + 5);
    doc.text('Cuarentena', margin + 115, y + 5);
    doc.text('Rechazado', margin + 138, y + 5);
    doc.text('Total', margin + 158, y + 5);
    doc.text('Eficacia %', margin + 171, y + 5);

    y += 7;

    // Draw lines
    statsByAuxiliar.forEach((item, index) => {
      // Create a page break if table grows too long (A4 height is 297mm)
      if (y > 255) {
        doc.addPage();
        y = 15;
        
        // Redraw thin header on next page
        doc.setFillColor(241, 245, 249);
        doc.rect(margin, y, 180, 7, 'F');
        doc.setTextColor(71, 85, 105);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(7.5);
        doc.text('Nombre Auxiliar (Completo)', margin + 3, y + 5);
        doc.text('Tiempo Medio', margin + 68, y + 5);
        doc.text('Aprobado', margin + 94, y + 5);
        doc.text('Cuarentena', margin + 115, y + 5);
        doc.text('Rechazado', margin + 138, y + 5);
        doc.text('Total', margin + 158, y + 5);
        doc.text('Eficacia %', margin + 171, y + 5);
        y += 7;
      }

      // Draw standard row background
      if (index % 2 === 0) {
        doc.setFillColor(250, 250, 250);
        doc.rect(margin, y, 180, 8, 'F');
      }
      doc.setDrawColor(241, 245, 249);
      doc.rect(margin, y, 180, 8, 'S');

      doc.setTextColor(15, 23, 42);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(7.5);
      doc.text(item.nombre, margin + 3, y + 5);

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7);
      doc.text(item.formattedDuration, margin + 68, y + 5);
      doc.text(item.aprobado.toLocaleString(), margin + 94, y + 5);
      doc.text(item.cuarentena.toLocaleString(), margin + 115, y + 5);
      doc.text(item.rechazado.toLocaleString(), margin + 138, y + 5);
      doc.text(item.total.toLocaleString(), margin + 158, y + 5);

      const score = item.efectividad;
      if (score >= 95) {
        doc.setTextColor(16, 185, 129);
      } else if (score >= 80) {
        doc.setTextColor(245, 158, 11);
      } else {
        doc.setTextColor(239, 68, 68);
      }
      doc.setFont('helvetica', 'bold');
      doc.text(`${score}%`, margin + 171, y + 5);

      y += 8;
    });

    y += 10;

    if (y > 240) {
      doc.addPage();
      y = 15;
    }

    // Tiempos por estado
    doc.setTextColor(30, 41, 59);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text('TIEMPO PROMEDIO DE DECISIÓN POR ESTADO TÉCNICO', margin, y);
    doc.line(margin, y + 2, margin + 180, y + 2);

    y += 6;

    // Card group for times
    avgTimeByState.forEach((stateInfo) => {
      doc.setFillColor(248, 250, 252);
      doc.rect(margin, y, 180, 8, 'F');
      doc.setDrawColor(241, 245, 249);
      doc.rect(margin, y, 180, 8, 'S');

      doc.setTextColor(15, 23, 42);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(7.5);
      doc.text(stateInfo.name, margin + 4, y + 5);

      doc.setFont('helvetica', 'normal');
      doc.text(`${stateInfo.formatted} (${stateInfo.count} lotes)`, margin + 140, y + 5);

      y += 9;
    });

    y += 4;

    if (y > 210) {
      doc.addPage();
      y = 15;
    }

    // CONTROL DE CAMBIOS Y ACTIVIDAD OPERATIVA (INVIMA)
    doc.setTextColor(30, 41, 59);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text('RESUMEN DE AUDITORÍA Y CONTROL DE SUCESOS (INVIMA)', margin, y);
    doc.line(margin, y + 2, margin + 180, y + 2);

    y += 6;

    const metricsSummary = [
      { name: 'ÓRDENES DE FABRICACIÓN CREADAS', count: auditMonthlyCounts.ordenesCreadas, color: [20, 110, 110] },
      { name: 'ÓRDENES DE FABRICACIÓN MODIFICADAS Y ACCIONES DE FLUJO', count: auditMonthlyCounts.ordenesModificadas, color: [2, 100, 150] },
      { name: 'ÓRDENES DE FABRICACIÓN CANCELADAS', count: auditMonthlyCounts.ordenesCanceladas, color: [180, 80, 10] },
      { name: 'DISPENSACIONES / DISPOSICIONES REGISTRADAS', count: auditMonthlyCounts.dispensacionesCreadas, color: [70, 40, 150] }
    ];

    metricsSummary.forEach((m) => {
      doc.setFillColor(248, 250, 252);
      doc.rect(margin, y, 180, 8, 'F');
      doc.setDrawColor(241, 245, 249);
      doc.rect(margin, y, 180, 8, 'S');

      doc.setTextColor(50, 60, 70);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(7.5);
      doc.text(m.name, margin + 4, y + 5);

      doc.setTextColor(m.color[0], m.color[1], m.color[2]);
      doc.setFont('helvetica', 'bold');
      doc.text(`${m.count} sucesos registrados`, margin + 130, y + 5);

      y += 9;
    });

    y = Math.min(265, y + 10);

    // Get current user signature image
    const getSignatureImageWithFallback = (userObj: User | null | undefined): string | null => {
      if (!userObj) return null;
      if (userObj.firma) return userObj.firma;

      try {
        const canvas = document.createElement('canvas');
        canvas.width = 300;
        canvas.height = 100;
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.clearRect(0, 0, 300, 100);
          ctx.strokeStyle = '#1d4ed8'; // blue-700
          ctx.lineWidth = 1.8;
          ctx.lineCap = 'round';
          ctx.lineJoin = 'round';

          let hash = 0;
          const name = userObj.nombreCompleto;
          for (let i = 0; i < name.length; i++) {
            hash = name.charCodeAt(i) + ((hash << 5) - hash);
          }
          const absHash = Math.abs(hash);

          ctx.beginPath();
          let x = 40;
          const yCoord = 50;
          ctx.moveTo(x, yCoord);
          
          const loops = Math.min(6, Math.max(3, name.split(' ')[0].length));
          for (let j = 0; j < loops; j++) {
            const nextX = x + 12 + (absHash % (j + 4));
            const topCurve = yCoord - 12 - (absHash % 8);
            const bottomCurve = yCoord + 12 + (absHash % 6);
            ctx.bezierCurveTo(x + 4, topCurve, nextX - 4, bottomCurve, nextX, yCoord - (absHash % 4));
            x = nextX;
          }

          ctx.bezierCurveTo(x + 20, yCoord + 10, x + 30, yCoord - 20, x + 40, yCoord);
          ctx.stroke();

          ctx.beginPath();
          ctx.lineWidth = 1.3;
          ctx.moveTo(35, yCoord + 10 + (absHash % 5));
          ctx.bezierCurveTo(100, yCoord + 24, x, yCoord + 5, x + 35, yCoord + 14);
          ctx.stroke();

          return canvas.toDataURL('image/png');
        }
      } catch (e) {
        console.error("Error generating fallback signature:", e);
      }
      return null;
    };

    const currentSig = getSignatureImageWithFallback(currentUser);
    if (currentSig) {
      try {
        doc.addImage(currentSig, 'PNG', margin + 10, y - 18, 45, 14);
        doc.addImage(currentSig, 'PNG', margin + 120, y - 18, 45, 14);
      } catch (err) {
        console.error("Error drawing dashboard signature:", err);
      }
    }

    doc.setDrawColor(200, 200, 200);
    doc.line(margin, y, margin + 70, y);
    doc.line(margin + 110, y, margin + 180, y);

    doc.setTextColor(100, 116, 139);
    doc.setFontSize(6.5);
    doc.setFont('helvetica', 'normal');
    doc.text('Firma de Regente de Farmacia o Director Técnico', margin, y + 4);
    doc.text('Responsable de Aseguramiento de Calidad', margin + 110, y + 4);

    doc.setFontSize(6);
    doc.text('Documento de Calidad Farmaceutica e Indicadores Insumedic S.A.S. - Autogenerado electronicamente', margin, 285);

    doc.save(`INSUMEDIC_Reporte_Dashboard_${selectedMonth}_${new Date().toISOString().split('T')[0]}.pdf`);
  };

  const handleExportDashboardExcel = () => {
    const headers = [
      'Nombre del Auxiliar',
      'Unidosis Aprobadas',
      'Unidosis en Cuarentena',
      'Unidosis Rechazadas',
      'Total Unidosis',
      'Lotes Totales',
      'Efectividad (%)',
      'Tiempo Promedio (Horas)',
      'Tiempo Promedio Formateado'
    ];

    const rows = statsByAuxiliar.map(item => [
      item.nombre,
      item.aprobado,
      item.cuarentena,
      item.rechazado,
      item.total,
      item.lotesTotal,
      `${item.efectividad}%`,
      item.avgElapsedHrs,
      item.formattedDuration
    ]);

    const periodLabel = getMonthLabel(selectedMonth);

    const titleRows = [
      ['REPORTE DE PRODUCTIVIDAD Y EFECTIVIDAD - INSUMEDIC S.A.S.'],
      [`Periodo de analisis:;${periodLabel}`],
      [`Fecha de reporte:;${new Date().toLocaleString('es-CO')}`],
      [`Total Unidosis Evaluadas:;${kpis.total}`],
      [`Efectividad Global:;${kpis.efectividadGlobal}%`],
      [''],
      ['RESUMEN DE AUDITORÍA Y CONTROL DE CAMBIOS (CUMPLIMIENTO INVIMA)'],
      [`Ordenes Creadas:;${auditMonthlyCounts.ordenesCreadas}`],
      [`Ordenes Modificadas/Acciones de Flujo:;${auditMonthlyCounts.ordenesModificadas}`],
      [`Ordenes Canceladas:;${auditMonthlyCounts.ordenesCanceladas}`],
      [`Dispensaciones/Disposiciones Registradas:;${auditMonthlyCounts.dispensacionesCreadas}`],
      [`Total Sucesos Mes:;${auditMonthlyCounts.totalSucesos}`],
      [''],
      headers.join(';')
    ];

    const csvContent = [
      ...titleRows.map(r => Array.isArray(r) ? r.join(';') : r),
      ...rows.map(row => row.map(val => {
        const strVal = String(val ?? '');
        const escaped = strVal.replace(/"/g, '""');
        return `"${escaped}"`;
      }).join(';'))
    ].join('\n');

    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `INSUMEDIC_Analisis_Productividad_${selectedMonth}_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="space-y-8 animate-fade-in" id="dashboard-productividad-container">
      
      {/* Brand Header Banner with corporate blue palette */}
      <div className="bg-[#071A36] border border-[#1C4F8A]/50 text-white p-6 rounded-2xl shadow-md flex flex-col md:flex-row justify-between items-start md:items-center gap-4 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 bg-teal-500/10 rounded-full blur-3xl pointer-events-none"></div>
        <div>
          <h2 className="text-xl font-extrabold font-sans flex items-center tracking-normal text-white">
            <BarChart3 className="h-6 w-6 mr-2.5 text-teal-400" />
            ANÁLISIS DE EFECTIVIDAD Y TIEMPOS DE RESPUESTA
          </h2>
          <p className="text-xs text-slate-200 font-sans mt-1 font-medium leading-relaxed max-w-2xl">
            Control integrado de la efectividad del Auxiliar de Reempaque. Mide las cantidades de unidosis liberadas en cada estado técnico y el tiempo promedio transcurrido desde la creación de la orden hasta la firma de disposición.
          </p>
        </div>
        <div>
          <span className="text-[10px] font-mono bg-[#1C4F8A]/60 text-teal-300 px-3 py-1.5 rounded-lg border border-[#1C4F8A] font-bold uppercase tracking-wider block text-center">
            Módulo de Calidad Integral
          </span>
        </div>
      </div>

      {/* Month dropdown selector row and Export Controls */}
      <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-2xs flex flex-col lg:flex-row lg:items-center justify-between gap-4 font-sans">
        <div className="flex items-center space-x-2.5">
          <Calendar className="h-5 w-5 text-blue-600 shrink-0" />
          <div>
            <span className="text-[10px] font-bold text-slate-400 block uppercase tracking-wider leading-none">Filtrar período de producción</span>
            <strong className="text-sm text-slate-800">Visualización de Historial por Mes (Firma de Inspector)</strong>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full lg:w-auto">
          {/* Select Period */}
          <div className="w-full sm:w-64">
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              className="w-full h-10 px-3 text-xs bg-slate-50 border border-slate-200 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-blue-500/15 focus:ring-blue-600 text-slate-850 font-bold cursor-pointer transition-all"
              id="select-dashboard-month"
            >
              <option value="ALL">🗓️ {getMonthLabel('ALL')}</option>
              {uniqueMonths.map(monthKey => (
                <option key={monthKey} value={monthKey}>
                  🗓️ {getMonthLabel(monthKey)}
                </option>
              ))}
            </select>
          </div>

          {/* Export buttons */}
          <div className="flex items-center gap-2">
            {(currentUser?.cargo === 'Director Técnico y de Calidad' || currentUser?.cargo === 'Regente Farmacéutico' || currentUser?.esAdmin || currentUser?.cargo === 'Administrador del Sistema') && (
              <button
                onClick={handleDownloadPDF}
                className="flex-1 sm:flex-none inline-flex items-center justify-center gap-1.5 h-10 px-3.5 bg-red-650 hover:bg-red-700 text-white text-xs font-bold rounded-lg transition-colors cursor-pointer shadow-xs whitespace-nowrap"
                title="Descargar análisis consolidado en PDF"
                id="btn-download-dashboard-pdf"
              >
                <Download className="h-4 w-4" />
                <span>Descargar PDF</span>
              </button>
            )}

            <button
              onClick={handleExportDashboardExcel}
              className="flex-1 sm:flex-none inline-flex items-center justify-center gap-1.5 h-10 px-3.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-lg transition-colors cursor-pointer shadow-xs whitespace-nowrap"
              title="Exportar datos del análisis a Excel (CSV)"
              id="btn-export-dashboard-excel"
            >
              <FileSpreadsheet className="h-4 w-4" />
              <span>Exportar Excel</span>
            </button>
          </div>
        </div>
      </div>

      {/* Dynamic KPI summary widgets */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" id="grid-dashboard-kpis">
        
        {/* KPI 1 - Average Cycle Time */}
        <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-3xs flex items-center space-x-4">
          <div className="p-3 rounded-xl bg-blue-50 text-blue-600 shrink-0">
            <Clock className="h-6 w-6" />
          </div>
          <div>
            <span className="text-[10px] uppercase font-bold text-slate-400 block tracking-wider font-sans">
              Tiempo Promedio de Decisión
            </span>
            <strong className="text-xl font-bold font-sans text-blue-900 block leading-tight">
              {kpis.avgElapsedStr}
            </strong>
            <span className="text-[9px] text-slate-500 font-medium block mt-0.5">
              Desde creación hasta firma técnica
            </span>
          </div>
        </div>

        {/* KPI 2 - Checked Units */}
        <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-3xs flex items-center space-x-4">
          <div className="p-3 rounded-xl bg-slate-50 text-slate-600 shrink-0">
            <Package className="h-6 w-6" />
          </div>
          <div>
            <span className="text-[10px] uppercase font-bold text-slate-400 block tracking-wider font-sans">
              Unidosis Evaluadas
            </span>
            <strong className="text-xl font-bold font-sans text-slate-805 block leading-tight">
              {kpis.total.toLocaleString()} ud.
            </strong>
            <span className="text-[9px] text-slate-500 font-medium block mt-0.5">
              En {kpis.lotesCount} lotes auditados
            </span>
          </div>
        </div>

        {/* KPI 3 - Approved Units */}
        <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-3xs flex items-center space-x-4">
          <div className="p-3 rounded-xl bg-emerald-50 text-emerald-600 shrink-0">
            <CheckCircle className="h-6 w-6" />
          </div>
          <div>
            <span className="text-[10px] uppercase font-bold text-slate-400 block tracking-wider font-sans">
              Porcentaje Aprobado
            </span>
            <strong className="text-xl font-bold font-sans text-emerald-600 block leading-tight">
              {kpis.aprobado.toLocaleString()} ud.
            </strong>
            <span className="text-[9px] text-emerald-600 font-bold block mt-0.5">
              Tasa de Eficacia: {kpis.efectividadGlobal}%
            </span>
          </div>
        </div>

        {/* KPI 4 - Non-compliance rate */}
        <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-3xs flex items-center space-x-4">
          <div className="p-3 rounded-xl bg-red-50 text-red-650 shrink-0">
            <ShieldAlert className="h-6 w-6" />
          </div>
          <div>
            <span className="text-[10px] uppercase font-bold text-slate-400 block tracking-wider font-sans">
              Rechazos y Cuarentena
            </span>
            <strong className="text-xl font-bold font-sans text-red-700 block leading-tight">
              {(kpis.cuarentena + kpis.rechazado).toLocaleString()} ud.
            </strong>
            <span className="text-[9px] text-red-600 font-bold block mt-0.5">
              Tasa Descarte: {kpis.total > 0 ? Math.round(((kpis.cuarentena + kpis.rechazado) / kpis.total) * 100) : 0}%
            </span>
          </div>
        </div>

      </div>

      {disposiciones.length === 0 ? (
        <div className="bg-white border border-slate-200 p-12 rounded-2xl text-center text-slate-500 font-sans space-y-3">
          <Award className="h-10 w-10 text-slate-400 mx-auto animate-pulse" />
          <p className="text-sm font-semibold text-slate-800">Esperando datos de Liberación de Lotes</p>
          <p className="text-xs text-slate-400 max-w-sm mx-auto">
            Por favor, acepte órdenes de producción en el módulo de liberación y registre al menos una disposición técnica para visualizar el control de tiempos de ciclo y efectividad de los auxiliares.
          </p>
        </div>
      ) : filteredDisposiciones.length === 0 ? (
        <div className="bg-white border border-slate-200 p-12 rounded-2xl text-center text-slate-500 font-sans">
          No hay registros de disposición firmados por inspectores en el período de <strong>{getMonthLabel(selectedMonth)}</strong>. 
          Seleccione otro período en el menú superior.
        </div>
      ) : (
        /* Workspace when data is registered */
        <div className="space-y-8">
          
          {/* Row of detailed effectiveness list & graphs */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
            
            {/* Table: Desempeño por Auxiliar (7/12 cols) */}
            <div className="lg:col-span-12 xl:col-span-7 bg-white p-6 rounded-2xl border border-slate-200 shadow-xs space-y-4">
              <div className="border-b border-slate-100 pb-3 flex justify-between items-center flex-wrap gap-2">
                <div>
                  <h3 className="font-sans font-extrabold text-slate-900 text-base">
                    Tabla de Desempeño, Tiempos y Efectividad por Auxiliar
                  </h3>
                  <p className="text-xs text-slate-500 font-sans mt-0.5">
                    Clasificación operativa basada en la velocidad de liberación y calidad final del termosellado.
                  </p>
                </div>
                <span className="px-2.5 py-1 bg-blue-50 text-blue-750 border border-blue-100 font-mono text-[10px] rounded-full font-bold">
                  Auxiliares Operando: {statsByAuxiliar.length}
                </span>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse" id="tabla-efectividad-auxiliar">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-50/50">
                      <th className="p-3 text-[10px] font-bold text-slate-500 uppercase tracking-wider font-sans">Nombre del Auxiliar</th>
                      <th className="p-3 text-[10px] font-bold text-slate-500 uppercase tracking-wider text-center font-sans">Tiempo Medio de Proceso</th>
                      <th className="p-3 text-[10px] font-bold text-slate-500 uppercase tracking-wider text-center font-sans">Cantidades por Estado (unidosis)</th>
                      <th className="p-3 text-[10px] font-bold text-slate-500 uppercase tracking-wider text-right font-sans">Total</th>
                      <th className="p-3 text-[10px] font-bold text-slate-500 uppercase tracking-wider text-right font-sans">Efectividad %</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 font-sans">
                    {statsByAuxiliar.map((item) => {
                      // Score color thresholds
                      const val = item.efectividad;
                      let scoreBg = 'bg-red-100 text-red-800';
                      let barColor = 'bg-red-500';
                      let scoreLabel = 'Bajo';

                      if (val >= 95) {
                        scoreBg = 'bg-emerald-100 text-emerald-800';
                        barColor = 'bg-emerald-500';
                        scoreLabel = 'Excelente';
                      } else if (val >= 80) {
                        scoreBg = 'bg-amber-100 text-amber-800';
                        barColor = 'bg-amber-500';
                        scoreLabel = 'Aceptable';
                      }

                      return (
                        <tr key={item.nombre} className="hover:bg-slate-50/70 transition-colors">
                          {/* Name \& Title */}
                          <td className="p-3">
                            <div className="flex items-center space-x-2.5">
                              <div className="h-8 w-8 rounded-full bg-slate-100 text-slate-600 flex items-center justify-center font-bold text-xs shrink-0">
                                <UserIcon className="h-4.5 w-4.5 text-slate-500" />
                              </div>
                              <div>
                                <strong className="text-xs text-slate-800 font-bold block">{item.nombre}</strong>
                                <span className="text-[10px] text-slate-400 block font-medium">Auxiliar de Reempaque</span>
                              </div>
                            </div>
                          </td>

                          {/* Average process cycle time for this packaging assistant */}
                          <td className="p-3 text-center">
                            <span className="inline-flex items-center px-2 py-1 rounded-lg bg-blue-50 text-blue-800 border border-blue-105 text-[10px] font-extrabold font-mono">
                              <Clock className="h-3 w-3 mr-1 text-blue-600 shrink-0" />
                              {item.formattedDuration}
                            </span>
                          </td>
                          
                          {/* Quantities of Approved, Quarantined, and Rejected units */}
                          <td className="p-3 text-center">
                            <div className="inline-flex items-center gap-1.5 justify-center">
                              {/* Approved badge */}
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-800 border border-emerald-100 text-[10px] font-bold" title="Aprobado">
                                <Check className="h-2.5 w-2.5 mr-0.5 text-emerald-600 shrink-0" />
                                {item.aprobado.toLocaleString()}
                              </span>

                              {/* Quarantined badge */}
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-amber-50 text-amber-800 border border-amber-100 text-[10px] font-bold" title="En Cuarentena">
                                <AlertTriangle className="h-2.5 w-2.5 mr-0.5 text-amber-600 shrink-0" />
                                {item.cuarentena.toLocaleString()}
                              </span>

                              {/* Rejected badge */}
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-red-50 text-red-800 border border-red-100 text-[10px] font-bold" title="Rechazado">
                                <XOctagon className="h-2.5 w-2.5 mr-0.5 text-red-650 shrink-0" />
                                {item.rechazado.toLocaleString()}
                              </span>
                            </div>
                          </td>

                          {/* Total Units Disposed */}
                          <td className="p-3 text-right">
                            <strong className="text-xs font-mono font-bold text-slate-800 block">
                              {item.total.toLocaleString()}
                            </strong>
                            <span className="text-[9px] text-slate-400 block font-medium">
                              {item.lotesTotal} lotes
                            </span>
                          </td>

                          {/* Effectiveness % */}
                          <td className="p-3 text-right">
                            <div className="flex flex-col items-end space-y-1">
                              <div className="flex items-center space-x-1.5">
                                <strong className="text-xs font-mono font-bold text-slate-800">{item.efectividad}%</strong>
                                <span className={`px-1.5 py-0.5 rounded text-[8px] font-black uppercase tracking-wider ${scoreBg}`}>
                                  {scoreLabel}
                                </span>
                              </div>
                              {/* visual progress meter */}
                              <div className="w-20 h-1 bg-slate-100 rounded-full overflow-hidden">
                                <div 
                                  className={`h-full ${barColor} rounded-full transition-all duration-300`}
                                  style={{ width: `${item.efectividad}%` }}
                                />
                              </div>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="p-3 bg-slate-50 border border-slate-200/50 rounded-xl text-[10px] text-slate-600 font-sans leading-relaxed flex items-start space-x-2">
                <span className="text-xs">💡</span>
                <span>
                  <strong>Efectividad y Oportunidad:</strong> Se correlaciona el tiempo que le toma a la entidad técnica emitir un juicio definitivo con el porcentaje de unidades aprobadas. Una alta velocidad con alta tasa de rechazo puede indicar un lote problemático o fallas severas de reempaque.
                </span>
              </div>
            </div>

            {/* General Distribution & State Speed Board (5/12 cols) */}
            <div className="lg:col-span-12 xl:col-span-5 space-y-6">
              
              {/* Card A: Tiempo Promedio por Estado de Disposición */}
              <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-xs space-y-4">
                <div>
                  <h3 className="font-sans font-extrabold text-slate-900 text-sm">
                    Tiempo de Decisión Técnica por Estado
                  </h3>
                  <p className="text-xs text-slate-400 font-sans">
                    Tiempo promedio transcurrido desde la creación del lote hasta emitir cada decisión.
                  </p>
                </div>

                <div className="space-y-3 font-sans">
                  {avgTimeByState.map((stateInfo) => {
                    let indicatorBg = 'bg-emerald-500';
                    let textClass = 'text-emerald-700';
                    let barBg = 'bg-emerald-50';

                    if (stateInfo.name === 'En Cuarentena') {
                      indicatorBg = 'bg-amber-500';
                      textClass = 'text-amber-700';
                      barBg = 'bg-amber-50';
                    } else if (stateInfo.name === 'Rechazado') {
                      indicatorBg = 'bg-red-500';
                      textClass = 'text-red-700';
                      barBg = 'bg-red-50';
                    }

                    // Calculate rough proportion for the visual meter (standardized max of 48 hours)
                    const proportion = Math.min(100, Math.round((stateInfo.avgHrs / 48) * 100)) || 2;

                    return (
                      <div key={stateInfo.name} className="flex flex-col space-y-1.5 p-3 rounded-xl border border-slate-100 bg-slate-50/50">
                        <div className="flex items-center justify-between text-xs">
                          <div className="flex items-center space-x-2">
                            <span className={`h-2.5 w-2.5 rounded-full ${indicatorBg}`} />
                            <strong className="text-slate-700 font-bold">{stateInfo.name}</strong>
                          </div>
                          <div className="flex items-center space-x-1 font-mono">
                            <span className={`font-black ${textClass}`}>{stateInfo.formatted}</span>
                            <span className="text-slate-400">({stateInfo.count} lotes)</span>
                          </div>
                        </div>

                        {/* Speed range helper indicator */}
                        <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
                          <div 
                            className={`h-full ${indicatorBg} rounded-full transition-all duration-300`} 
                            style={{ width: `${proportion}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Card B: Donut Chart of Quantities */}
              <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-xs flex flex-col justify-between">
                <div className="space-y-1">
                  <h3 className="font-sans font-extrabold text-slate-900 text-sm">
                    Distribución Global de Aseguramiento
                  </h3>
                  <p className="text-xs text-slate-400 font-sans mb-3">
                    Proporción de unidades físicas liberadas por el Director Técnico.
                  </p>
                </div>

                {donutData.length === 0 ? (
                  <div className="py-8 text-center text-xs text-slate-400">Sin datos de clasificación.</div>
                ) : (
                  <div className="h-44 relative flex items-center justify-center my-2">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={donutData}
                          innerRadius={50}
                          outerRadius={68}
                          paddingAngle={5}
                          dataKey="value"
                        >
                          {donutData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip 
                          contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '11px', fontFamily: 'sans-serif' }}
                          formatter={(value) => [`${value.toLocaleString()} unidades`, 'Volumen']}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                    {/* Center percentage indicator */}
                    <div className="absolute text-center mt-[-4px] font-sans">
                      <span className="text-[8px] uppercase font-bold text-slate-400 block tracking-wider leading-none">
                        Efectividad
                      </span>
                      <span className="text-lg font-extrabold text-slate-800">
                        {kpis.efectividadGlobal}%
                      </span>
                    </div>
                  </div>
                )}

                {/* Custom descriptive legend of the donut */}
                <div className="space-y-1.5 border-t border-slate-100 pt-3 font-sans">
                  {donutData.map((item, idx) => {
                    const percent = kpis.total > 0 ? Math.round((item.value / kpis.total) * 100) : 0;
                    return (
                      <div key={idx} className="flex items-center justify-between text-[11px]">
                        <div className="flex items-center space-x-1.5">
                          <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: item.color }} />
                          <span className="text-slate-650 block font-medium">{item.name}</span>
                        </div>
                        <strong className="text-slate-850 font-bold">{item.value.toLocaleString()} ud. ({percent}%)</strong>
                      </div>
                    );
                  })}
                </div>
              </div>

            </div>

          </div>

          {/* Double Graph Section: Column Charts comparing both parameters */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            
            {/* Chart 1 (Bar Chart): Average process time (hours) by assistant */}
            <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm space-y-4">
              <div>
                <h3 className="font-sans font-extrabold text-slate-900 text-sm flex items-center">
                  <Clock className="h-4 w-4 mr-1.5 text-blue-600" />
                  Comparativo de Tiempo Promedio de Respuesta (Horas)
                </h3>
                <p className="text-xs text-slate-400 font-sans">
                  Tiempo promedio transcurrido (en horas) desde la creación de la orden hasta la decisión para cada auxiliar. ¡El objetivo institucional es menor a 24 horas!
                </p>
              </div>

              <div className="h-72 w-full pt-2 font-sans">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                    <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#64748b', fontWeight: 'bold' }} axisLine={false} tickLine={false} />
                    <YAxis label={{ value: 'Horas', angle: -90, position: 'insideLeft', offset: 0, style: { fontSize: 10, fill: '#64748b', fontWeight: 'bold' } }} tick={{ fontSize: 10, fill: '#64748b' }} axisLine={false} tickLine={false} />
                    <Tooltip 
                      contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '11px', fontFamily: 'sans-serif' }}
                      formatter={(value, name, props) => {
                        return [`${value} hrs (${props.payload.formattedTime})`, 'Tiempo Promedio'];
                      }}
                    />
                    <Bar dataKey="Tiempo Promedio (Horas)" fill="#004b87" radius={[4, 4, 0, 0]} maxBarSize={35}>
                      {chartData.map((entry, index) => {
                        const val = entry['Tiempo Promedio (Horas)'];
                        // High cycle time warning
                        const color = val > 24 ? '#1e3a8a' : '#004b87';
                        return <Cell key={`cell-${index}`} fill={color} />;
                      })}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Chart 2 (Bar Chart): Physical units of packings */}
            <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm space-y-4">
              <div>
                <h3 className="font-sans font-extrabold text-slate-900 text-sm flex items-center">
                  <Activity className="h-4 w-4 mr-1.5 text-emerald-600" />
                  Comparativo de Volumen de Unidosis de Auxiliares
                </h3>
                <p className="text-xs text-slate-400 font-sans">
                  Cantidad exacta de unidades aprobadas, en cuarentena y rechazadas acumuladas para cada operario.
                </p>
              </div>

              <div className="h-72 w-full pt-2 font-sans">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData} margin={{ top: 10, right: 10, left: -15, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                    <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#64748b', fontWeight: 'bold' }} axisLine={false} tickLine={false} />
                    <YAxis label={{ value: 'Unidades', angle: -90, position: 'insideLeft', offset: 5, style: { fontSize: 10, fill: '#64748b', fontWeight: 'bold' } }} tick={{ fontSize: 10, fill: '#64748b' }} axisLine={false} tickLine={false} />
                    <Tooltip 
                      contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '11px', fontFamily: 'sans-serif' }}
                      formatter={(value) => [`${value.toLocaleString()} unidades`, 'Volumen']}
                    />
                    <Legend wrapperStyle={{ fontSize: '10px', marginTop: '5px' }} />
                    <Bar dataKey="Aprobado" fill="#00cc00" radius={[3, 3, 0, 0]} maxBarSize={20} />
                    <Bar dataKey="En Cuarentena" fill="#f59e0b" radius={[3, 3, 0, 0]} maxBarSize={20} />
                    <Bar dataKey="Rechazado" fill="#ef4444" radius={[3, 3, 0, 0]} maxBarSize={20} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

          </div>

        </div>
      )}

      {/* SECTION: Audit Tracking Space for Trazabilidad INVIMA */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm space-y-6 p-6 font-sans mt-8" id="audit-logs-section">
        
        {/* Header Block and Tab Selector */}
        <div className="border-b border-slate-100 pb-5 flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
          <div className="space-y-1">
            <h3 className="text-base font-extrabold text-slate-900 flex items-center gap-2">
              <History className="h-5 w-5 text-slate-700 font-bold" />
              Seguimiento de Auditoría y Control de Cambios (INVIMA)
            </h3>
            <p className="text-xs text-slate-500">
              Trazabilidad técnica requerida para certificar la cadena de custodia, flujos de firmas y estados en reempaque de medicamentos.
            </p>
          </div>
          
          {/* Sub-tabs Selector with smooth design */}
          <div className="flex bg-slate-100 p-1 rounded-xl border border-slate-200 shrink-0 select-none w-full lg:w-auto">
            <button
              onClick={() => setAuditTab('trazabilidad')}
              className={`flex-1 lg:flex-initial px-4 py-1.5 text-xs font-bold rounded-lg transition-all cursor-pointer ${
                auditTab === 'trazabilidad'
                  ? 'bg-white text-slate-900 shadow-xs'
                  : 'text-slate-500 hover:text-slate-850'
              }`}
              id="tab-toggle-trazabilidad"
            >
              📊 Trazabilidad por Orden
            </button>
            <button
              onClick={() => setAuditTab('bitacora')}
              className={`flex-1 lg:flex-initial px-4 py-1.5 text-xs font-bold rounded-lg transition-all cursor-pointer ${
                auditTab === 'bitacora'
                  ? 'bg-white text-slate-900 shadow-xs'
                  : 'text-slate-500 hover:text-slate-850'
              }`}
              id="tab-toggle-bitacora"
            >
              📝 Bitácora de Sucesos
            </button>
          </div>
        </div>

        {/* INVIMA Metrics Summary Row */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 bg-slate-50/60 p-4 rounded-xl border border-slate-200/80" id="invima-summary-metrics">
          <div className="bg-white p-3 rounded-lg border border-slate-200 shadow-3xs flex flex-col justify-between">
            <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wide">Órdenes Creadas</span>
            <div className="flex items-baseline gap-1.5 mt-1">
              <span className="text-lg font-black text-teal-700">{auditMonthlyCounts.ordenesCreadas}</span>
              <span className="text-[9px] text-teal-600 font-extrabold px-1.5 py-0.2 bg-teal-50 border border-teal-200/50 rounded-md">INVIMA</span>
            </div>
          </div>
          <div className="bg-white p-3 rounded-lg border border-slate-200 shadow-3xs flex flex-col justify-between">
            <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wide">Órdenes Modificadas</span>
            <div className="flex items-baseline gap-1.5 mt-1">
              <span className="text-lg font-black text-sky-700">{auditMonthlyCounts.ordenesModificadas}</span>
              <span className="text-[9px] text-sky-600 font-extrabold px-1.5 py-0.2 bg-sky-50 border border-sky-250 rounded-md">INVIMA</span>
            </div>
          </div>
          <div className="bg-white p-3 rounded-lg border border-slate-200 shadow-3xs flex flex-col justify-between">
            <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wide">Órdenes Canceladas</span>
            <div className="flex items-baseline gap-1.5 mt-1">
              <span className="text-lg font-black text-amber-700">{auditMonthlyCounts.ordenesCanceladas}</span>
              <span className="text-[9px] text-amber-600 font-extrabold px-1.5 py-0.2 bg-amber-50 border border-amber-200/50 rounded-md">INVIMA</span>
            </div>
          </div>
          <div className="bg-white p-3 rounded-lg border border-slate-200 shadow-3xs flex flex-col justify-between">
            <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wide">Dispensaciones Reg.</span>
            <div className="flex items-baseline gap-1.5 mt-1">
              <span className="text-lg font-black text-violet-700">{auditMonthlyCounts.dispensacionesCreadas}</span>
              <span className="text-[9px] text-violet-600 font-extrabold px-1.5 py-0.2 bg-violet-50 border border-violet-200/50 rounded-md">INVIMA</span>
            </div>
          </div>
          <div className="bg-white p-3 rounded-lg border border-slate-200 shadow-3xs flex flex-col justify-between col-span-2 md:col-span-1">
            <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wide">Total Sucesos</span>
            <div className="flex items-baseline gap-1.5 mt-1">
              <span className="text-lg font-black text-slate-800">{auditMonthlyCounts.totalSucesos}</span>
              <span className="text-[9px] text-slate-500 font-medium whitespace-nowrap">Auditado</span>
            </div>
          </div>
        </div>

        {/* --- VIEW 1: TRAZABILIDAD CRONOLÓGICA POR ORDEN --- */}
        {auditTab === 'trazabilidad' ? (
          <div className="space-y-6" id="view-trazabilidad-ordenes">
            
            {/* Search and Action control bar */}
            <div className="flex flex-col md:flex-row justify-between items-stretch md:items-center gap-3 bg-slate-50/70 p-4 rounded-xl border border-slate-200/60" id="traced-filter-ctrls">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                <input
                  type="text"
                  placeholder="Buscar por N° Orden, Lote, Medicamento, Auxiliar o Firma..."
                  value={tracedSearch}
                  onChange={(e) => { setTracedSearch(e.target.value); setTracedPage(1); }}
                  className="w-full h-10 pl-9 pr-3 text-xs bg-white border border-slate-200 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-slate-500/15 focus:ring-slate-600 text-slate-800 transition-all placeholder:text-slate-400"
                  id="input-traced-search"
                />
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <button
                  onClick={handleDownloadTracedPDF}
                  className="inline-flex items-center justify-center gap-1.5 h-10 px-3.5 bg-rose-650 hover:bg-rose-700 text-white text-xs font-bold rounded-lg transition-colors cursor-pointer shadow-xs whitespace-nowrap"
                  title="Descargar reporte detallado de trazabilidad en PDF"
                  id="btn-download-traced-pdf"
                >
                  <Download className="h-4 w-4" />
                  <span>Descargar PDF de Trazabilidad</span>
                </button>
                <button
                  onClick={handleExportTracedExcel}
                  className="inline-flex items-center justify-center gap-1.5 h-10 px-3.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-lg transition-colors cursor-pointer shadow-xs whitespace-nowrap"
                  title="Exportar matriz de trazabilidad a CSV / Excel"
                  id="btn-export-traced-excel"
                >
                  <FileSpreadsheet className="h-4 w-4" />
                  <span>Exportar Excel</span>
                </button>
              </div>
            </div>

            {/* List and timeline of orders with F1, F2 & F3 tracking */}
            <div className="overflow-x-auto rounded-xl border border-slate-200 shadow-3xs" id="traced-table-container">
              <table className="w-full text-left border-collapse" id="tabla-trazabilidad-ordenes">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50/80">
                    <th className="p-3.5 text-[10px] font-bold text-slate-500 uppercase tracking-wider font-sans w-[210px]">Estructura de la Orden</th>
                    <th className="p-3.5 text-[10px] font-bold text-slate-500 uppercase tracking-wider font-sans">Fase 1: Solicitud (Registro)</th>
                    <th className="p-3.5 text-[10px] font-bold text-slate-500 uppercase tracking-wider font-sans">Fase 2: Aceptación (Aprobación)</th>
                    <th className="p-3.5 text-[10px] font-bold text-slate-500 uppercase tracking-wider font-sans">Fase 3: Liberación (Disposición)</th>
                    <th className="p-3.5 text-[10px] font-bold text-slate-500 uppercase tracking-wider font-sans text-right w-[110px]">Estado Actual</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 font-sans text-slate-700 text-xs">
                  {paginatedTracedOrders.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="p-10 text-center text-xs text-slate-400 bg-white">
                        No se encontraron órdenes que coincidan con la búsqueda de trazabilidad.
                      </td>
                    </tr>
                  ) : (
                    paginatedTracedOrders.map((ord) => {
                      const dateSolFormatted = ord.solicitud.fecha 
                        ? new Date(ord.solicitud.fecha).toISOString().replace('T', ' ').substring(0, 19)
                        : 'Sin Registro';
                      const dateAcFormatted = ord.aceptacion.completado && ord.aceptacion.fecha
                        ? new Date(ord.aceptacion.fecha).toISOString().replace('T', ' ').substring(0, 19)
                        : '';
                      const dateLibFormatted = ord.liberacion.completado && ord.liberacion.fecha
                        ? new Date(ord.liberacion.fecha).toISOString().replace('T', ' ').substring(0, 19)
                        : '';

                      return (
                        <tr key={ord.id} className="hover:bg-slate-50/40 transition-colors">
                          
                          {/* Col 1: Identification */}
                          <td className="p-4">
                            <div className="space-y-1">
                              <span className="font-extrabold text-slate-900 block" id={`traced-op-${ord.numeroOrden}`}>
                                OP N° {ord.numeroOrden}
                              </span>
                              <span className="text-[10px] font-semibold text-slate-500 block">
                                Lote RP: <strong className="font-bold text-slate-700">{ord.loteRP || 'N/A'}</strong>
                              </span>
                              <span className="text-[10.5px] text-slate-600 font-medium line-clamp-1 block" title={ord.nombreMedicamento}>
                                {ord.nombreMedicamento}
                              </span>
                            </div>
                          </td>

                          {/* Col 2: Fase 1 (Solicitud) */}
                          <td className="p-4 bg-emerald-50/15">
                            <div className="flex items-start gap-2">
                              <div className="p-1 rounded-full bg-emerald-50 text-emerald-600 shrink-0 mt-0.5 border border-emerald-100">
                                <Check className="h-3 w-3" />
                              </div>
                              <div className="space-y-0.5">
                                <span className="text-[10.5px] font-bold text-slate-800 uppercase tracking-wider block">Solicitud Solicitada</span>
                                <span className="text-[10px] font-mono text-slate-500 block">{dateSolFormatted}</span>
                                <span className="text-[11px] font-semibold text-slate-600 flex items-center gap-1 shrink-0">
                                  <UserIcon className="h-3 w-3 text-slate-400" />
                                  <span>{ord.solicitud.usuario}</span>
                                </span>
                              </div>
                            </div>
                          </td>

                          {/* Col 3: Fase 2 (Aceptación) */}
                          <td className={`p-4 ${ord.aceptacion.completado ? 'bg-indigo-50/15' : 'bg-amber-50/10'}`}>
                            {ord.aceptacion.completado ? (
                              <div className="flex items-start gap-2">
                                <div className="p-1 rounded-full bg-indigo-50 text-indigo-600 shrink-0 mt-0.5 border border-indigo-100">
                                  <Check className="h-3 w-3" />
                                </div>
                                <div className="space-y-0.5">
                                  <span className="text-[10.5px] font-bold text-slate-800 uppercase tracking-wider block">Aceptada en Línea</span>
                                  <span className="text-[10px] font-mono text-slate-500 block">{dateAcFormatted}</span>
                                  <span className="text-[11px] font-semibold text-slate-600 flex items-center gap-1 shrink-0">
                                    <UserIcon className="h-3 w-3 text-slate-400" />
                                    <span>{ord.aceptacion.usuario}</span>
                                  </span>
                                </div>
                              </div>
                            ) : (
                              <div className="flex items-start gap-2">
                                <div className="p-1 rounded-full bg-amber-50 text-amber-600 shrink-0 mt-0.5 border border-amber-100 animate-pulse">
                                  <Hourglass className="h-3 w-3" />
                                </div>
                                <div className="space-y-0.5">
                                  <span className="text-[10.5px] font-bold text-amber-700 uppercase tracking-wider block">Pendiente de Aprobación</span>
                                  <span className="text-[10px] text-slate-400 block">—</span>
                                  <span className="text-[10.5px] text-slate-400 block italic">Por Director Técnico</span>
                                </div>
                              </div>
                            )}
                          </td>

                          {/* Col 4: Fase 3 (Liberación / Disposición) */}
                          <td className={`p-4 ${ord.liberacion.completado ? 'bg-cyan-50/15' : 'bg-slate-50/20'}`}>
                            {ord.liberacion.completado ? (
                              <div className="flex items-start gap-2">
                                <div className="p-1 rounded-full bg-cyan-50 text-cyan-600 shrink-0 mt-0.5 border border-cyan-100">
                                  <Check className="h-3 w-3 font-semibold" />
                                </div>
                                <div className="space-y-0.5">
                                  <span className="text-[10.5px] font-bold text-slate-800 uppercase tracking-wider block">Liberado (Co. Calidad)</span>
                                  <span className="text-[10px] font-mono text-slate-500 block">{dateLibFormatted}</span>
                                  <span className="text-[11px] font-semibold text-slate-600 flex items-center gap-1 shrink-0">
                                    <UserIcon className="h-3 w-3 text-slate-400" />
                                    <span>{ord.liberacion.usuario}</span>
                                  </span>
                                </div>
                              </div>
                            ) : (
                              <div className="flex items-start gap-2">
                                <div className="p-1 rounded-full bg-slate-150 text-slate-400 shrink-0 mt-0.5">
                                  <Hourglass className="h-3 w-3" />
                                </div>
                                <div className="space-y-0.5">
                                  <span className="text-[10.5px] font-bold text-slate-400 uppercase tracking-wider block">Pendiente liberar</span>
                                  <span className="text-[10px] text-slate-400 block">—</span>
                                  <span className="text-[10.5px] text-slate-400 block italic">Falta Certificación</span>
                                </div>
                              </div>
                            )}
                          </td>

                          {/* Col 5: Current status */}
                          <td className="p-4 text-right">
                            <span className={`px-2 py-0.5 border text-[10px] rounded-full font-bold uppercase ${
                              ord.estadoActual === 'Cerrada' 
                                ? 'bg-slate-100 text-slate-700 border-slate-200'
                                : ord.estadoActual === 'Aceptada'
                                ? 'bg-indigo-50 text-indigo-700 border-indigo-150'
                                : ord.estadoActual === 'Cancelada'
                                ? 'bg-rose-50 text-rose-700 border-rose-150'
                                : 'bg-amber-50 text-amber-700 border-amber-150'
                            }`}>
                              {ord.estadoActual}
                            </span>
                          </td>

                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination for Traced Orders view */}
            <div className="flex items-center justify-between font-sans pt-3 border-t border-slate-100" id="traced-pagination">
              <div className="text-[11px] text-slate-450 font-medium">
                Resumen: <strong>{paginatedTracedOrders.length}</strong> de <strong>{processedTracedOrders.length}</strong> órdenes en el historial de trazabilidad.
              </div>
              
              <div className="flex items-center space-x-1.5">
                <button
                  onClick={() => setTracedPage(prev => Math.max(prev - 1, 1))}
                  disabled={tracedPage === 1}
                  className={`h-8 px-2.5 text-xs font-bold rounded-lg border ${
                    tracedPage === 1
                      ? 'bg-slate-50 text-slate-300 cursor-not-allowed border-slate-105'
                      : 'bg-white text-slate-705 hover:bg-slate-50 cursor-pointer border-slate-200'
                  } transition-colors`}
                  id="btn-prev-traced-page"
                >
                  Anterior
                </button>
                <div className="px-3 text-xs font-bold text-slate-705">
                  Pág. {tracedPage} de {totalTracedPages}
                </div>
                <button
                  onClick={() => setTracedPage(prev => Math.min(prev + 1, totalTracedPages))}
                  disabled={tracedPage === totalTracedPages}
                  className={`h-8 px-2.5 text-xs font-bold rounded-lg border ${
                    tracedPage === totalTracedPages
                      ? 'bg-slate-50 text-slate-300 cursor-not-allowed border-slate-105'
                      : 'bg-white text-slate-705 hover:bg-slate-50 cursor-pointer border-slate-200'
                  } transition-colors`}
                  id="btn-next-traced-page"
                >
                  Siguiente
                </button>
              </div>
            </div>

          </div>
        ) : (
          /* --- VIEW 2: BITÁCORA DE SUCESOS GENERAL --- */
          <div className="space-y-6" id="view-bitacora-sucesos">
            
            {/* Filters and Search Workspace Container */}
            <div className="bg-slate-50/70 p-4 rounded-xl border border-slate-200/60 space-y-3.5" id="audit-filters-panel">
              <div className="flex items-center gap-2 text-xs font-bold text-slate-700">
                <SlidersHorizontal className="h-3.5 w-3.5 text-slate-500" />
                <span>Filtros de Trazabilidad Dinámicos</span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                {/* Search inputs */}
                <div className="relative">
                  <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                  <input
                    type="text"
                    placeholder="Buscar por detalle o palabra clave..."
                    value={auditSearch}
                    onChange={(e) => { setAuditSearch(e.target.value); setAuditPage(1); }}
                    className="w-full h-10 pl-9 pr-3 text-xs bg-white border border-slate-200 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-slate-500/15 focus:ring-slate-600 text-slate-800 transition-all placeholder:text-slate-400"
                    id="input-audit-search"
                  />
                </div>

                {/* Filter by Module */}
                <div>
                  <select
                    value={auditModuleFilter}
                    onChange={(e) => { setAuditModuleFilter(e.target.value); setAuditPage(1); }}
                    className="w-full h-10 px-3 text-xs bg-white border border-slate-200 rounded-lg focus:outline-hidden text-slate-705 font-medium cursor-pointer transition-all"
                    id="select-audit-module"
                  >
                    <option value="ALL">📦 Todos los Módulos ({uniqueModulesInLogs.length})</option>
                    {uniqueModulesInLogs.map(m => (
                      <option key={m} value={m}>📦 Módulo: {m}</option>
                    ))}
                  </select>
                </div>

                {/* Filter by Action */}
                <div>
                  <select
                    value={auditActionFilter}
                    onChange={(e) => { setAuditActionFilter(e.target.value); setAuditPage(1); }}
                    className="w-full h-10 px-3 text-xs bg-white border border-slate-200 rounded-lg focus:outline-hidden text-slate-750 font-medium cursor-pointer transition-all"
                    id="select-audit-action"
                  >
                    <option value="ALL">⚡ Todas las Acciones ({uniqueActionsInLogs.length})</option>
                    {uniqueActionsInLogs.map(act => (
                      <option key={act} value={act}>⚡ {act}</option>
                    ))}
                  </select>
                </div>

                {/* Filter by User */}
                <div>
                  <select
                    value={auditUserFilter}
                    onChange={(e) => { setAuditUserFilter(e.target.value); setAuditPage(1); }}
                    className="w-full h-10 px-3 text-xs bg-white border border-slate-200 rounded-lg focus:outline-hidden text-slate-750 font-medium cursor-pointer transition-all"
                    id="select-audit-user"
                  >
                    <option value="ALL">👤 Todos los Responsables ({uniqueUsersInLogs.length})</option>
                    {uniqueUsersInLogs.map(u => (
                      <option key={u} value={u}>👤 {u}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Reset Filters alert box */}
              {(auditSearch || auditModuleFilter !== 'ALL' || auditActionFilter !== 'ALL' || auditUserFilter !== 'ALL') && (
                <div className="flex items-center justify-between text-xs font-semibold text-slate-655 pt-1" id="audit-filters-alert">
                  <span>Filtros activos. Se encontraron <strong>{processedLogs.length}</strong> de <strong>{tenantFilteredLogs.length}</strong> registros auditables.</span>
                  <button
                    onClick={handleResetAuditFilters}
                    className="text-blue-600 hover:text-blue-700 hover:underline font-bold font-sans cursor-pointer bg-none border-none outline-hidden"
                    id="btn-reset-audit-filters"
                  >
                    Limpiar todo
                  </button>
                </div>
              )}
            </div>

            {/* Audit Log Table visualization */}
            <div className="overflow-x-auto rounded-xl border border-slate-250/80">
              <table className="w-full text-left border-collapse" id="tabla-logs-auditoria">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50/80">
                    <th className="p-3 text-[10px] font-bold text-slate-500 uppercase tracking-wider font-sans w-[145px]">FECHA Y HORA (UTC)</th>
                    <th className="p-3 text-[10px] font-bold text-slate-500 uppercase tracking-wider font-sans w-[180px]">USUARIO RESPONSABLE</th>
                    <th className="p-3 text-[10px] font-bold text-slate-500 uppercase tracking-wider font-sans w-[120px]">MÓDULO</th>
                    <th className="p-3 text-[10px] font-bold text-slate-500 uppercase tracking-wider font-sans w-[160px]">TIPO DE ACCIÓN</th>
                    <th className="p-3 text-[10px] font-bold text-slate-500 uppercase tracking-wider font-sans">DETALLE DE LA OPERACIÓN / ACTIVIDAD QUE SE REALIZÓ</th>
                    <th className="p-3 text-[10px] font-bold text-slate-500 uppercase tracking-wider font-sans w-[120px] text-right">DIRECCIÓN IP</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 font-sans text-slate-705 text-xs">
                  {paginatedLogs.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="p-8 text-center text-xs text-slate-400 bg-white">
                        Ningún registro de cambio o modificación coincide con los filtros especificados.
                      </td>
                    </tr>
                  ) : (
                    paginatedLogs.map((log) => {
                      let moduleColor = 'bg-slate-100 text-slate-700 border-slate-200';
                      if (log.modulo === 'Órdenes') {
                        moduleColor = 'bg-blue-50 text-blue-700 border-blue-100';
                      } else if (log.modulo === 'Disposición') {
                        moduleColor = 'bg-emerald-50 text-emerald-700 border-emerald-100';
                      } else if (log.modulo === 'Usuarios') {
                        moduleColor = 'bg-amber-50 text-amber-700 border-amber-100';
                      } else if (log.modulo === 'Seguridad') {
                        moduleColor = 'bg-rose-50 text-rose-700 border-rose-100';
                      } else if (log.modulo?.includes('Copias') || log.modulo === 'Copias Seguridad' || log.modulo === 'Sistema') {
                        moduleColor = 'bg-purple-50 text-purple-700 border-purple-100';
                      }

                      const dateFormatted = new Date(log.timestamp).toISOString().replace('T', ' ').substring(0, 19);

                      return (
                        <tr key={log.id} className="hover:bg-slate-50/50 transition-colors">
                          {/* Date */}
                          <td className="p-3 whitespace-nowrap text-slate-500 font-mono text-[10px]">
                            {dateFormatted}
                          </td>

                          {/* User */}
                          <td className="p-3 font-semibold text-slate-900">
                            <div className="flex items-center gap-1.5">
                              <div className="h-5.5 w-5.5 bg-slate-100 rounded-full flex items-center justify-center font-bold text-[9px] text-slate-600 uppercase shrink-0">
                                {log.usuario ? log.usuario.substring(0, 2) : 'US'}
                              </div>
                              <span className="truncate max-w-[150px]" title={log.usuario}>{log.usuario}</span>
                            </div>
                          </td>

                          {/* Module */}
                          <td className="p-3 whitespace-nowrap">
                            <span className={`px-2 py-0.5 border text-[9px] rounded-full font-bold uppercase tracking-wider ${moduleColor}`}>
                              {log.modulo}
                            </span>
                          </td>

                           {/* Action Type */}
                          <td className="p-3 whitespace-nowrap">
                            <span className={`px-2 py-1 text-[9px] rounded-md font-extrabold uppercase tracking-wider border ${
                              log.tipoAccion === 'ELIMINADO'
                                ? 'bg-rose-50 text-rose-700 border-rose-200'
                                : log.tipoAccion === 'CANCELADO'
                                ? 'bg-amber-50 text-amber-800 border-amber-200/80'
                                : log.tipoAccion === 'INICIO DE SESION'
                                ? 'bg-emerald-50 text-emerald-700 border-emerald-250'
                                : log.tipoAccion === 'ASIGNACION DE MODULO'
                                ? 'bg-indigo-50 text-indigo-700 border-indigo-200'
                                : log.tipoAccion === 'CREACION DE USUARIOS'
                                ? 'bg-cyan-50 text-cyan-700 border-cyan-200'
                                : log.tipoAccion === 'CREACION DE ORDENES'
                                ? 'bg-teal-50 text-teal-700 border-teal-250'
                                : log.tipoAccion === 'MODIFICACION DE ORDENES'
                                ? 'bg-sky-50 text-sky-700 border-sky-205'
                                : log.tipoAccion === 'REGISTRO DE DISPOSICION'
                                ? 'bg-violet-50 text-violet-700 border-violet-200'
                                : log.tipoAccion === 'MODIFICACION DE DISPOSICION'
                                ? 'bg-fuchsia-50 text-fuchsia-700 border-fuchsia-250'
                                : 'bg-blue-50 text-blue-700 border-blue-200'
                            }`}>
                              {log.tipoAccion}
                            </span>
                          </td>

                          {/* Details */}
                          <td className="p-3 text-slate-600 max-w-sm font-medium leading-relaxed font-sans break-words pr-4">
                            {log.detalle}
                          </td>

                          {/* IP address */}
                          <td className="p-3 whitespace-nowrap text-right text-slate-400 font-mono text-[10px]">
                            {log.ipAddress}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Table Pagination */}
            <div className="flex items-center justify-between font-sans pt-3 border-t border-slate-100">
              <div className="text-[11px] text-slate-450 font-medium">
                Mostrando <strong>{paginatedLogs.length}</strong> de <strong>{processedLogs.length}</strong> registros encontrados (Total acumulado de la empresa: {tenantFilteredLogs.length})
              </div>
              
              <div className="flex items-center space-x-1.5">
                <button
                  onClick={() => setAuditPage(prev => Math.max(prev - 1, 1))}
                  disabled={auditPage === 1}
                  className={`h-8 px-2.5 text-xs font-bold rounded-lg border ${
                    auditPage === 1
                      ? 'bg-slate-50 text-slate-300 cursor-not-allowed border-slate-100'
                      : 'bg-white text-slate-705 hover:bg-slate-50 cursor-pointer border-slate-200'
                  } transition-colors`}
                  id="btn-prev-audit-page"
                >
                  Anterior
                </button>
                <div className="px-3 text-xs font-bold text-slate-705">
                  Pág. {auditPage} de {totalAuditPages}
                </div>
                <button
                  onClick={() => setAuditPage(prev => Math.min(prev + 1, totalAuditPages))}
                  disabled={auditPage === totalAuditPages}
                  className={`h-8 px-2.5 text-xs font-bold rounded-lg border ${
                    auditPage === totalAuditPages
                      ? 'bg-slate-50 text-slate-300 cursor-not-allowed border-slate-100'
                      : 'bg-white text-slate-705 hover:bg-slate-50 cursor-pointer border-slate-200'
                  } transition-colors`}
                  id="btn-next-audit-page"
                >
                  Siguiente
                </button>
              </div>
            </div>

          </div>
        )}

      </div>

    </div>
  );
}
